import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Box,
  Autocomplete,
  FormControlLabel,
  Switch,
  Checkbox,
  CircularProgress,
  Alert,
  Paper,
  Link,
  Chip,
  Tooltip,
  Snackbar,
  FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import CategoryIcon from '@mui/icons-material/Category';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import BusinessIcon from '@mui/icons-material/Business';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InventoryCategoryModal from './InventoryCategoryModal';
import InventoryBrandModal from './InventoryBrandModal';
import InventoryWarehouseModal from './InventoryWarehouseModal';
import ImageLibrarySelectionModal from './ImageLibrarySelectionModal';
// Firebase kaldırıldı - Para birimleri artık SQL API'den yükleniyor
import { useAuth } from '../../../contexts/AuthContext';
import { UNIT_OPTIONS } from '../../../constants/units';
import ItemUnitsEditor, { type ItemUnitsEditorHandle } from '../../production/components/shared/ItemUnitsEditor';
import { InventoryItemUnitError } from '../../production/services/inventoryItemUnitService';
// SQL Services
import { InventoryService, CreateInventoryItemDto } from './services/inventoryService';
import { 
  CategoryService, 
  InventoryCategoryDto 
} from './services/categoryService';
import { 
  BrandService, 
  InventoryBrandDto 
} from './services/brandService';
import { 
  WarehouseService, 
  InventoryWarehouseDto 
} from './services/warehouseService';
import {
  ProductFeatureService,
  type InventoryProductFeatureDto,
  type SaveInventoryItemFeatureValueRowDto
} from './services/productFeatureService';
import { smartWarehouseService } from '../../wms/services/smartWarehouseService';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import { COUNTRIES, TURKEY_CODE, countryCodeToFlag } from '../../../utils/countries';
import { generateInternalBarcode } from '../../../utils/ean13';
import { useTranslation } from 'react-i18next';

interface AddInventoryItemProps {
  open: boolean;
  onClose: () => void;
  branchId?: string;
  itemToEdit?: any; // ✅ YENİ: Düzenleme modu için - any kullanarak type uyumsuzluğunu çöz
  onItemAdded?: (itemId: string, itemName: string) => void; // ✅ YENİ: Ürün eklendikten sonra callback
  onItemUpdated?: (item: InventoryItem) => void;
  prefillName?: string; // ✅ YENİ: Ürün adını otomatik doldur (e-fatura için)
}

interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
  isParent?: boolean;
  label?: string;
}

interface Warehouse {
  id: string;
  name: string;
  address?: string;
  description?: string;
  isDefault?: boolean;
  branchesId?: string | null;
  warehouseType?: string;
}

interface Brand {
  id: string;
  name: string;
  description?: string;
  website?: string;
  branchesId?: string | null;
}

// Local Branch interface (AddInventoryItem için özel)
interface LocalBranch {
  id: string;
  name: string;
  address?: string;
  isHeadquarter?: boolean;
}

interface ExtraFeatureRow {
  tempId: string;
  featureId: string;
  value: string;
}

interface InventoryItem {
  id: string;
  name: string;
  barcode?: string;
  categoryId?: string | null;
  brandId?: string | null;
  stockCode: string;
  // ⚠️ DEPRECATED: Backend'den gelecek ama frontend'de kullanmayacağız
  quantity?: number; // Geçici - WarehouseStock'tan gelecek
  warehouseId?: string | null; // Geçici - WarehouseStock'tan gelecek
  unit: string;
  purchasePrice: number;
  purchaseCurrency?: string;
  purchasePrice2: number;
  purchaseCurrency2?: string;
  purchasePrice3: number;
  purchaseCurrency3?: string;
  purchaseVendor: string;
  purchaseVendor2: string;
  purchaseVendor3: string;
  salePrice: number;
  saleCurrency?: string;
  salePrice2: number;
  saleCurrency2?: string;
  salePrice3: number;
  saleCurrency3?: string;
  saleCustomer: string;
  saleCustomer2: string;
  saleCustomer3: string;
  desi?: number;
  vatRate?: number;
  vatStatus?: string;
  sctRate?: number;
  sctStatus?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  branchesId?: string | null;
  parentItemId?: string | null;
  // LEGACY UYUM: isMainItem karar mekanizmasi degil, gecis alani.
  isMainItem?: boolean;
  isVariantParent?: boolean;
  departmentNo?: number | null;
  // ✅ YENİ SİSTEM: Depo stokları
  warehouseStocks?: {
    id: string;
    productId: string;
    warehouseId: string;
    warehouseName?: string;
    quantity: number;
    createdAt: string;
    updatedAt?: string;
  }[];
}

const normalizeImageUrls = (rawImageUrl: unknown, rawImageUrls: unknown): string[] => {
  const collected: string[] = [];

  if (typeof rawImageUrl === 'string' && rawImageUrl.trim()) {
    collected.push(...rawImageUrl.split(',').map(x => x.trim()).filter(Boolean));
  }

  if (Array.isArray(rawImageUrls)) {
    collected.push(...rawImageUrls.map(x => String(x).trim()).filter(Boolean));
  } else if (typeof rawImageUrls === 'string' && rawImageUrls.trim()) {
    const asText = rawImageUrls.trim();
    try {
      const parsed = JSON.parse(asText);
      if (Array.isArray(parsed)) {
        collected.push(...parsed.map(x => String(x).trim()).filter(Boolean));
      } else {
        collected.push(...asText.split(',').map(x => x.trim()).filter(Boolean));
      }
    } catch {
      collected.push(...asText.split(',').map(x => x.trim()).filter(Boolean));
    }
  }

  return Array.from(new Set(collected));
};

const AddInventoryItem: React.FC<AddInventoryItemProps> = ({ open, onClose, branchId, itemToEdit, onItemAdded, onItemUpdated, prefillName }) => {
  const { t } = useTranslation('accounting');
  // ✅ Düzenleme modu kontrolü
  const isEditMode = Boolean(itemToEdit);
  
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al (global branches ve userDetails)
  const { branches: globalBranches, userDetails, isModuleEnabled } = useAuth();
  const productionEnabled = isModuleEnabled('production');
  const lotTrackingEnabled = isModuleEnabled('lot_tracking');
  
  // 🔍 OFFLINE DEBUG
  const hasLoggedRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !hasLoggedRef.current) {
      hasLoggedRef.current = true;
      console.log('🚀 [ADD-ITEM] Modal açıldı');
      console.log('🔍 [ADD-ITEM] AuthContext userDetails:', userDetails ? `✅ companyId: ${userDetails.companyId}` : '❌ YOK');
    }
    if (!open) {
      hasLoggedRef.current = false;
    }
  }, [open, userDetails]);
  
  // ✅ YENİ: E-fatura'dan gelen ürün adını otomatik doldur
  React.useEffect(() => {
    if (open && prefillName && !isEditMode) {
      console.log('🏷️ [ADD-ITEM] E-fatura ürün adı otomatik dolduruluyor:', prefillName);
      setItemName(prefillName);
    }
  }, [open, prefillName, isEditMode]);

  // ✅ YENİ: Düzenleme modu - itemToEdit geldiğinde form alanlarını doldur
  React.useEffect(() => {
    if (open && itemToEdit && isEditMode) {
      console.log('📝 [ADD-ITEM] Düzenleme modu - form dolduruluyor:', itemToEdit);
      
      // Temel bilgiler
      setItemName(itemToEdit.name || '');
      setBarcode(itemToEdit.barcode || '');
      setIsDomesticProduct((itemToEdit as any).isDomesticProduct === true);
      setCountryOfOrigin((itemToEdit as any).countryOfOrigin || '');
      setIsScale((itemToEdit as any).isScale === true);
      setScaleCode((itemToEdit as any).scaleCode || '');
      setShelfLifeDays(
        (itemToEdit as any).shelfLifeDays ? String((itemToEdit as any).shelfLifeDays) : ''
      );
      setTrackLots((itemToEdit as any).trackLots === true || (itemToEdit as any).TrackLots === true);
      setTrackExpiry((itemToEdit as any).trackExpiry === true || (itemToEdit as any).TrackExpiry === true);
      setStockCode(itemToEdit.stockCode || '');
      setCategoryId(itemToEdit.categoryId || null);
      setBrandId(itemToEdit.brandId || null);
      setUnit(itemToEdit.unit || 'adet');
      setDescription(itemToEdit.description || '');
      setBranchesId(itemToEdit.branchesId || null);
      setImageUrls(normalizeImageUrls((itemToEdit as any).imageUrl, (itemToEdit as any).imageUrls));
      
      // ⚠️ KRİTİK: Stok eksiye düşebilir ayarı
      // Backend'den canBeNegative veya CanBeNegative olarak gelebilir
      const canBeNegativeValue = itemToEdit.canBeNegative ?? itemToEdit.CanBeNegative ?? true;
      setCanBeNegative(canBeNegativeValue);
      console.log('🔒 [ADD-ITEM] canBeNegative:', canBeNegativeValue, '(raw:', itemToEdit.canBeNegative, itemToEdit.CanBeNegative, ')');

      setIsVariantParent(itemToEdit.isVariantParent === true);
      
      // Fiyat bilgileri (Temel)
      setPurchasePrice(itemToEdit.purchasePrice || '');
      setPurchaseCurrency(itemToEdit.purchaseCurrency || 'TRY');
      setPurchaseVendor(itemToEdit.purchaseVendor || '');
      setSalePrice(itemToEdit.salePrice || '');
      setSaleCurrency(itemToEdit.saleCurrency || 'TRY');
      setSaleCustomer(itemToEdit.saleCustomer || '');
      
      // ✅ Ek Fiyat Bilgileri (purchasePrice2, purchasePrice3, listPrice, vb.)
      setPurchasePrice2(itemToEdit.purchasePrice2 || '');
      setPurchaseCurrency2(itemToEdit.purchaseCurrency2 || 'TRY');
      setPurchaseVendor2(itemToEdit.purchaseVendor2 || '');
      setPurchasePrice3(itemToEdit.purchasePrice3 || '');
      setPurchaseCurrency3(itemToEdit.purchaseCurrency3 || 'TRY');
      setPurchaseVendor3(itemToEdit.purchaseVendor3 || '');
      
      setListPrice((itemToEdit as any).listPrice || '');
      setListCurrency((itemToEdit as any).listCurrency || 'TRY');
      
      setSalePrice2(itemToEdit.salePrice2 || '');
      setSaleCurrency2(itemToEdit.saleCurrency2 || 'TRY');
      setSaleCustomer2(itemToEdit.saleCustomer2 || '');
      setSalePrice3(itemToEdit.salePrice3 || '');
      setSaleCurrency3(itemToEdit.saleCurrency3 || 'TRY');
      setSaleCustomer3(itemToEdit.saleCustomer3 || '');
      
      // Vergi bilgileri
      // FIX: `||` yerine `??` kullanılıyor: `||` operatörü 0 değerini falsy sayıp sessizce 20'ye çeviriyordu;
      //      `??` sadece null/undefined'da default'a düşer, gerçek 0 oranı korunur. Boş kayıtlarda eski
      //      davranışla uyumlu olarak %20 önerilir (Türkiye standart KDV).
      // FIX: vatStatus için orijinal default 'INCLUSIVE' (uppercase) idi; dropdown ve backend lowercase
      //      'included'/'excluded' kullanıyor, bu yüzden hiçbir <MenuItem>'a eşleşmiyor ve dropdown boş
      //      görünüyordu. Boş gelirse boş bırakıyoruz, zorunlu alan olarak kullanıcı doldurur.
      setVatRate(itemToEdit.vatRate ?? 20);
      setVatStatus(itemToEdit.vatStatus ?? '');
      setSctRate(itemToEdit.sctRate ?? 0);
      setSctStatus(itemToEdit.sctStatus ?? '');
      setDepartmentNo(itemToEdit.departmentNo || '');
      setItemType(itemToEdit.itemType === 'SERVICE' ? 'SERVICE' : 'PRODUCT');
      setItemClass(itemToEdit.itemClass ?? itemToEdit.ItemClass ?? '');
      setProductionMode((itemToEdit.productionMode ?? itemToEdit.ProductionMode) === 'MAKE_TO_STOCK' ? 'MAKE_TO_STOCK' : 'SALE_EXPLODE');
      setVariantDesi(itemToEdit.desi || '');
      
      // ✅ Varyant Özellikleri (varsa set et)
      if ((itemToEdit as any).variants) {
        const variants = (itemToEdit as any).variants;
        const physical = variants?.physical ?? variants;
        
        // Fiziksel Özellikler
        setVariantColor(physical.color || '');
        setVariantSize(physical.size || '');
        setVariantLength(physical.length || '');
        setVariantWidth(physical.width || '');
        setVariantHeight(physical.height || '');
        setVariantWeight(physical.weight || '');
        setVariantDesi(itemToEdit.desi || physical.desi || '');
        setVariantThickness(physical.thickness || '');
        
        // Teknik Özellikler
        setVariantMaterial(variants.material || '');
        setVariantGrade(variants.grade || '');
        setVariantModel(variants.model || '');
        setVariantSeries(variants.series || '');
        setVariantPower(variants.power || '');
        setVariantVoltage(variants.voltage || '');
        
        // Tasarım Özellikleri
        setVariantPattern(variants.pattern || '');
        setVariantStyle(variants.style || '');
        setVariantFinish(variants.finish || '');
        setVariantShape(variants.shape || '');
        
        // Kapasite ve Boyut
        setVariantCapacity(variants.capacity || '');
        setVariantDiameter(variants.diameter || '');
        setVariantInnerSize(variants.innerSize || '');
        setVariantOuterSize(variants.outerSize || '');
        
        // Kullanım Özellikleri
        setVariantAgeGroup(variants.ageGroup || '');
        setVariantGender(variants.gender || '');
        setVariantSeason(variants.season || '');
        setVariantUsage(variants.usage || '');
        
        // Ürün Çeşitleri
        setVariantFlavor(variants.flavor || '');
        setVariantScent(variants.scent || '');
        setVariantHardness(variants.hardness || '');
        setVariantTransparency(variants.transparency || '');
        
        // Paketleme Özellikleri
        setVariantPackQuantity(variants.packQuantity || '');
        setVariantPackType(variants.packType || '');
        setVariantPackSize(variants.packSize || '');
        
        // Özel Özellikler
        setVariantWaterproof(variants.waterproof ?? null);
        setVariantAntibacterial(variants.antibacterial ?? null);
        setVariantOrganic(variants.organic ?? null);
        setVariantRecyclable(variants.recyclable ?? null);
        
        console.log('✅ [ADD-ITEM] Varyant özellikleri yüklendi');
      }
      
      // Depo stokları: ilk kaydı kör seçmek yerine en yüksek pozitif miktarı seç.
      if (itemToEdit.warehouseStocks && itemToEdit.warehouseStocks.length > 0) {
        const stocks = [...itemToEdit.warehouseStocks];
        const positiveStocks = stocks
          .filter((s: any) => Number(s.quantity ?? 0) > 0)
          .sort((a: any, b: any) => Number(b.quantity ?? 0) - Number(a.quantity ?? 0));
        const positiveWithLocation = positiveStocks.filter((s: any) => !!s.locationId);
        const primaryStock =
          positiveWithLocation[0] ||
          positiveStocks[0] ||
          stocks[0];

        setWarehouseId(primaryStock.warehouseId ?? null);
        setQuantity(primaryStock.quantity ?? '');
        setSelectedLocationId(primaryStock.locationId ?? null);
      }
    }
  }, [open, itemToEdit, isEditMode]);
  
  // ✅ LocalBranch type'a dönüştürme (address ve isHeadquarter için)
  const branches: LocalBranch[] = React.useMemo(() => {
    return globalBranches.map(b => ({
      id: b.id,
      name: b.name,
      address: '',
      isHeadquarter: false
    }));
  }, [globalBranches]);
  
  // ✅ "Kaydet ve Yeni Ekle" sonrası başarı bildirimi (modal açık kaldığı için)
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [unitNotice, setUnitNotice] = useState<{ message: string; severity: 'info' | 'error' } | null>(null);
  const [baseUnitLocked, setBaseUnitLocked] = useState(false);
  const unitsEditorRef = useRef<ItemUnitsEditorHandle>(null);

  // Form state
  const [itemName, setItemName] = useState('');
  const [barcode, setBarcode] = useState('');
  const [isDomesticProduct, setIsDomesticProduct] = useState<boolean>(false);
  const [countryOfOrigin, setCountryOfOrigin] = useState<string>('');
  // Tartı: kod barkodun ortasındaki 5 hane, baştaki sıfırlar anlamlı
  const [isScale, setIsScale] = useState<boolean>(false);
  const [scaleCode, setScaleCode] = useState<string>('');
  const [shelfLifeDays, setShelfLifeDays] = useState<string>('');
  const [trackLots, setTrackLots] = useState(false);
  const [trackExpiry, setTrackExpiry] = useState(false);

  // Girilen gün sayısının etikette hangi tarihe denk geldiğini gösterir.
  // Cihaz "tartım günü + (değer - 1)" hesapladığı için 1 = bugün.
  const shelfLifeDaysHelperText = React.useMemo(() => {
    const days = parseInt(shelfLifeDays, 10);
    if (!Number.isFinite(days) || days <= 0) {
      return 'Boş bırakılırsa etikete son tüketim tarihi basılmaz.';
    }
    const target = new Date();
    target.setDate(target.getDate() + days - 1);
    return `Bugün dahil ${days} gün — bugün tartılsa etikette ${target.toLocaleDateString('tr-TR')} yazar.`;
  }, [shelfLifeDays]);

  // Tartı kodu iki işi birlikte yapıyor: ağırlık barkodunun ortasına basılan
  // kod ve tartıda ürünü çağırma numarası (PLU). Aynı sayı olduğu için
  // kullanıcıya ikisini birlikte söylüyoruz.
  const scaleCodeHelperText = React.useMemo(() => {
    const digits = scaleCode.replace(/\D/g, '');
    if (!digits) {
      return 'Boş bırakılırsa tartıya gönderimde otomatik atanır.';
    }
    const plu = parseInt(digits, 10);
    const padded = digits.padStart(5, '0');
    return `Tartıda PLU ${plu} olarak kaydedilir; barkodun ortasına ${padded} basılır.`;
  }, [scaleCode]);
  const [stockCode, setStockCode] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number | string>('');
  const [canBeNegative, setCanBeNegative] = useState<boolean>(true);
  const [isVariantParent, setIsVariantParent] = useState<boolean>(false);
  const [unit, setUnit] = useState('adet');
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [purchasePrice, setPurchasePrice] = useState<number | ''>( '');
  const [purchaseCurrency, setPurchaseCurrency] = useState('TRY');
  const [purchaseVendor, setPurchaseVendor] = useState('');
  const [purchasePrice2, setPurchasePrice2] = useState<number | ''>('');
  const [purchaseCurrency2, setPurchaseCurrency2] = useState('TRY');
  const [purchaseVendor2, setPurchaseVendor2] = useState('');
  const [purchasePrice3, setPurchasePrice3] = useState<number | ''>('');
  const [purchaseCurrency3, setPurchaseCurrency3] = useState('TRY');
  const [purchaseVendor3, setPurchaseVendor3] = useState('');
  const [salePrice, setSalePrice] = useState<number | ''>('');
  const [saleCurrency, setSaleCurrency] = useState('TRY');
  const [saleCustomer, setSaleCustomer] = useState('');
  const [listPrice, setListPrice] = useState<number | ''>('');
  const [listCurrency, setListCurrency] = useState('TRY');
  const [salePrice2, setSalePrice2] = useState<number | ''>('');
  const [saleCurrency2, setSaleCurrency2] = useState('TRY');
  const [saleCustomer2, setSaleCustomer2] = useState('');
  const [salePrice3, setSalePrice3] = useState<number | ''>('');
  const [saleCurrency3, setSaleCurrency3] = useState('TRY');
  const [saleCustomer3, setSaleCustomer3] = useState('');
  const [vatRate, setVatRate] = useState<number | ''>('');
  const [vatStatus, setVatStatus] = useState('');
  const [sctRate, setSctRate] = useState<number | ''>('');
  const [sctStatus, setSctStatus] = useState('excluded'); // excluded or included
  const [description, setDescription] = useState('');
  const [departmentNo, setDepartmentNo] = useState<number | ''>('');
  const [itemType, setItemType] = useState<'PRODUCT' | 'SERVICE'>('PRODUCT');
  const [itemClass, setItemClass] = useState('');
  const [productionMode, setProductionMode] = useState<'SALE_EXPLODE' | 'MAKE_TO_STOCK'>('SALE_EXPLODE');
  
  // Varyant özellikleri için state'ler
  const [selectedVariantCategories, setSelectedVariantCategories] = useState<string[]>([]);
  
  // Fiziksel Özellikler
  const [variantColor, setVariantColor] = useState('');
  const [variantSize, setVariantSize] = useState('');
  const [variantLength, setVariantLength] = useState<number | ''>('');
  const [variantWidth, setVariantWidth] = useState<number | ''>('');
  const [variantHeight, setVariantHeight] = useState<number | ''>('');
  const [variantWeight, setVariantWeight] = useState<number | ''>('');
  const [variantDesi, setVariantDesi] = useState<number | ''>('');
  const [showDesiCalculator, setShowDesiCalculator] = useState<boolean>(false);
  const [variantThickness, setVariantThickness] = useState<number | ''>('');
  
  // Teknik Özellikler
  const [variantMaterial, setVariantMaterial] = useState('');
  const [variantGrade, setVariantGrade] = useState('');
  const [variantModel, setVariantModel] = useState('');
  const [variantSeries, setVariantSeries] = useState('');
  const [variantPower, setVariantPower] = useState<number | ''>('');
  const [variantVoltage, setVariantVoltage] = useState<number | ''>('');
  
  // Tasarım Özellikleri
  const [variantPattern, setVariantPattern] = useState('');
  const [variantStyle, setVariantStyle] = useState('');
  const [variantFinish, setVariantFinish] = useState('');
  const [variantShape, setVariantShape] = useState('');
  
  // Kapasite ve Boyut
  const [variantCapacity, setVariantCapacity] = useState<number | ''>('');
  const [variantDiameter, setVariantDiameter] = useState<number | ''>('');
  const [variantInnerSize, setVariantInnerSize] = useState('');
  const [variantOuterSize, setVariantOuterSize] = useState('');
  
  // Kullanım Özellikleri
  const [variantAgeGroup, setVariantAgeGroup] = useState('');
  const [variantGender, setVariantGender] = useState('');
  const [variantSeason, setVariantSeason] = useState('');
  const [variantUsage, setVariantUsage] = useState('');
  
  // Ürün Çeşitleri
  const [variantFlavor, setVariantFlavor] = useState('');
  const [variantScent, setVariantScent] = useState('');
  const [variantHardness, setVariantHardness] = useState('');
  const [variantTransparency, setVariantTransparency] = useState('');
  
  // Paketleme Özellikleri
  const [variantPackQuantity, setVariantPackQuantity] = useState<number | ''>('');
  const [variantPackType, setVariantPackType] = useState('');
  const [variantPackSize, setVariantPackSize] = useState('');
  
  // Özel Özellikler
  const [variantWaterproof, setVariantWaterproof] = useState<boolean | null>(null);
  const [variantAntibacterial, setVariantAntibacterial] = useState<boolean | null>(null);
  const [variantOrganic, setVariantOrganic] = useState<boolean | null>(null);
  const [variantRecyclable, setVariantRecyclable] = useState<boolean | null>(null);
  const [showLegacyVariantFields] = useState<boolean>(false);
  
  // Şube seçimi için state
  const [selectedBranch, setSelectedBranch] = useState<LocalBranch | null>(null);
  const [branchesId, setBranchesId] = useState<string | null>(null);

  // Birim seçenekleri
  const unitOptions = [...UNIT_OPTIONS];
  const UNIT_TRANSLATION_KEYS: Record<string, string> = {
    adet: 'piece',
    kg: 'kg',
    gr: 'gram',
    lt: 'liter',
    ml: 'milliliter',
    m: 'meter',
    cm: 'centimeter',
    'm²': 'squareMeter',
    'm³': 'cubicMeter',
    'çift': 'pair',
    paket: 'pack',
    kutu: 'box',
    top: 'rollBundle',
    rulo: 'roll',
  };
  const getUnitLabel = (unitValue: string): string => {
    const key = UNIT_TRANSLATION_KEYS[unitValue];
    return key ? t(`stock.addModal.units.${key}`) : unitValue;
  };

  // Varyant kategori seçenekleri
  const variantCategoryOptions = [
    { value: 'physical', label: 'Fiziksel Özellikler' },
    { value: 'technical', label: 'Teknik Özellikler' },
    { value: 'design', label: 'Tasarım Özellikleri' },
    { value: 'capacity', label: 'Kapasite ve Boyut' },
    { value: 'usage', label: 'Kullanım Özellikleri' },
    { value: 'variety', label: 'Ürün Çeşitleri' },
    { value: 'packaging', label: 'Paketleme Özellikleri' },
    { value: 'special', label: 'Özel Özellikler' }
  ];

  // Varyant seçenek listleri
  const sizeOptions = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '28', '30', '32', '34', '36', '38', '40', '42', '44', '46', '48', '50'];
  const colorOptions = ['Kırmızı', 'Mavi', 'Yeşil', 'Sarı', 'Siyah', 'Beyaz', 'Gri', 'Mor', 'Turuncu', 'Pembe', 'Kahverengi', 'Lacivert'];
  const gradeOptions = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'Birinci Sınıf', 'İkinci Sınıf', 'Üçüncü Sınıf'];
  const patternOptions = ['Düz', 'Çizgili', 'Kareli', 'Puantiyeli', 'Çiçekli', 'Geometrik', 'Desenli'];
  const styleOptions = ['Modern', 'Klasik', 'Vintage', 'Minimalist', 'Rustic', 'Endüstriyel'];
  const finishOptions = ['Mat', 'Parlak', 'Saten', 'Yağlı', 'Su Bazlı', 'Doğal'];
  const shapeOptions = ['Yuvarlak', 'Kare', 'Dikdörtgen', 'Oval', 'Üçgen', 'Altıgen'];
  const ageGroupOptions = ['Bebek (0-2)', 'Çocuk (3-12)', 'Genç (13-17)', 'Yetişkin (18-64)', 'Yaşlı (65+)'];
  const genderOptions = ['Erkek', 'Kadın', 'Unisex', 'Çocuk'];
  const seasonOptions = ['İlkbahar', 'Yaz', 'Sonbahar', 'Kış', 'Tüm Mevsim'];
  const usageOptions = ['İç Mekan', 'Dış Mekan', 'Profesyonel', 'Ev Kullanımı', 'Endüstriyel'];
  const hardnessOptions = ['Çok Yumuşak', 'Yumuşak', 'Orta', 'Sert', 'Çok Sert'];
  const transparencyOptions = ['Şeffaf', 'Yarı Şeffaf', 'Mat', 'Opak'];
  const packTypeOptions = ['Tekli', 'İkili', 'Üçlü', 'Set', 'Paket', 'Kutu'];
  const packSizeOptions = ['Mini', 'Küçük', 'Orta', 'Büyük', 'XL', 'XXL'];

  // Veritabanı verileri için state'ler
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<InventoryCategory | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  
  // Yükleme durumları
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [showInfoBox, setShowInfoBox] = useState(false);
  
  // Para birimi seçenekleri için state
  const [availableCurrencies, setAvailableCurrencies] = useState<{code: string, active: boolean}[]>([
    { code: 'TRY', active: true } // Varsayılan olarak TRY her zaman olacak
  ]);

  // Modal açılış durumlarını kontrol eden state'ler
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [isImageLibraryModalOpen, setIsImageLibraryModalOpen] = useState(false);
  const [hasExtraFeatures, setHasExtraFeatures] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState<InventoryProductFeatureDto[]>([]);
  const [itemFeatureValues, setItemFeatureValues] = useState<ExtraFeatureRow[]>([]);

  // Barkod kontrolü için yeni state ekleyelim
  const [barcodeExists, setBarcodeExists] = useState(false);
  const [existingItem, setExistingItem] = useState<InventoryItem | null>(null);
  const [isDuplicateBarcodeModalOpen, setIsDuplicateBarcodeModalOpen] = useState(false);
  
  // Props olarak gelen branchId varsa modal açıldığında otomatik seç
  useEffect(() => {
    if (open && branchId) {
      setBranchesId(branchId);
    }
  }, [open, branchId]);

  // Şirket ID'sini ve verileri yükleme
  useEffect(() => {
    if (open) {
      setShowInfoBox(false);
      // ✅ Varsayılan değerleri SADECE YENİ ÜRÜN modunda ayarla
      // Düzenleme modunda itemToEdit'ten değerler gelecek
      if (!itemToEdit) {
        setUnit('adet');
        setVatRate('');
        setVatStatus('');
      }
      
      console.log('🔄 [ADD-ITEM] companyId ayarlanıyor...');
      
      // AuthContext'ten companyId al
      if (userDetails?.companyId) {
        console.log('✅ [ADD-ITEM] AuthContext\'ten companyId alındı:', userDetails.companyId);
        setCompanyId(userDetails.companyId);
        loadCurrencies(userDetails.companyId);
        setInitialLoading(false);
      } else {
        console.warn('⚠️ [ADD-ITEM] AuthContext\'te companyId yok, henüz yüklenmemiş olabilir');
        setInitialLoading(false);
      }
    }
    
    // Cleanup - Artık Firebase listener yok
    return () => {};
  }, [open, userDetails?.companyId]);

  useEffect(() => {
    const loadFeatureDefinitions = async () => {
      if (!open || !companyId) return;
      try {
        const features = await ProductFeatureService.getAll();
        setAvailableFeatures(features);
      } catch (featureError) {
        console.error('❌ [ADD-ITEM] Ek özellik tanımları yüklenemedi:', featureError);
      }
    };

    loadFeatureDefinitions();
  }, [open, companyId]);

  useEffect(() => {
    const loadItemFeatureValues = async () => {
      if (!open) return;

      if (isEditMode && itemToEdit?.id) {
        try {
          const values = await ProductFeatureService.getValuesByItemId(itemToEdit.id);
          if (values.length > 0) {
            setHasExtraFeatures(true);
            setItemFeatureValues(values.map((v, index) => ({
              tempId: `${v.id || index}-${v.featureId}`,
              featureId: v.featureId,
              value: v.value || '',
            })));
          } else {
            setHasExtraFeatures(false);
            setItemFeatureValues([]);
          }
        } catch (featureValueError) {
          console.error('❌ [ADD-ITEM] Ürün ek özellik değerleri yüklenemedi:', featureValueError);
          setHasExtraFeatures(false);
          setItemFeatureValues([]);
        }
      } else if (!isEditMode) {
        setHasExtraFeatures(false);
        setItemFeatureValues([]);
      }
    };

    loadItemFeatureValues();
  }, [open, isEditMode, itemToEdit?.id]);

  // Şube değiştiğinde kategorileri, markaları ve depoları yeniden yükle
  useEffect(() => {
    if (companyId && open) {
      loadCategories(companyId);
      loadWarehouses(companyId);
      loadBrands(companyId);
    }
  }, [branchesId, companyId, open]);

  // ✅ YENİ: Kategoriler yüklendiğinde ve categoryId varsa, seçili kategoriyi bul
  useEffect(() => {
    if (categories.length > 0 && categoryId && isEditMode) {
      const category = categories.find(c => c.id === categoryId);
      if (category) {
        setSelectedCategory(category);
        console.log('✅ [ADD-ITEM] Kategori eşleştirildi:', category.name);
      } else {
        console.warn('⚠️ [ADD-ITEM] Kategori bulunamadı:', categoryId);
      }
    }
  }, [categories, categoryId, isEditMode]);

  // ✅ YENİ: Markalar yüklendiğinde ve brandId varsa, seçili markayı bul
  useEffect(() => {
    if (brands.length > 0 && brandId && isEditMode) {
      const brand = brands.find(b => b.id === brandId);
      if (brand) {
        setSelectedBrand(brand);
        console.log('✅ [ADD-ITEM] Marka eşleştirildi:', brand.name);
      } else {
        console.warn('⚠️ [ADD-ITEM] Marka bulunamadı:', brandId);
      }
    }
  }, [brands, brandId, isEditMode]);

  // ✅ YENİ: Depolar yüklendiğinde ve warehouseId varsa, seçili depoyu bul
  useEffect(() => {
    if (warehouses.length > 0 && warehouseId && isEditMode) {
      const warehouse = warehouses.find(w => w.id === warehouseId);
      if (warehouse) {
        setSelectedWarehouse(warehouse);
        console.log('✅ [ADD-ITEM] Depo eşleştirildi:', warehouse.name);
      } else {
        console.warn('⚠️ [ADD-ITEM] Depo bulunamadı:', warehouseId);
      }
    }
  }, [warehouses, warehouseId, isEditMode]);

  // ✅ YENİ: Şubeler yüklendiğinde ve branchesId varsa, seçili şubeyi bul
  useEffect(() => {
    if (branches.length > 0 && branchesId && isEditMode) {
      const branch = branches.find(b => b.id === branchesId);
      if (branch) {
        setSelectedBranch(branch);
        console.log('✅ [ADD-ITEM] Şube eşleştirildi:', branch.name);
      } else {
        console.warn('⚠️ [ADD-ITEM] Şube bulunamadı:', branchesId);
      }
    }
  }, [branches, branchesId, isEditMode]);

  // 🔒 Miktar değiştiğinde depo seçimini kontrol et
  useEffect(() => {
    // Düzenleme modunda mevcut ürünün deposunu otomatik değiştirme.
    if (isEditMode) return;

    const quantityValue = quantity === '' ? 0 : Number(quantity);
    
    if (quantityValue > 0) {
      // Miktar > 0 ise Merkez Depo'yu otomatik seç
      if (warehouses.length > 0 && !selectedWarehouse) {
        const defaultWarehouse = warehouses.find(w => w.isDefault) || warehouses[0];
        setSelectedWarehouse(defaultWarehouse);
        setWarehouseId(defaultWarehouse.id);
        console.log('🔒 Miktar > 0, Merkez Depo otomatik seçildi:', defaultWarehouse.name);
      }
    } else {
      // Miktar = 0 ise depo seçimini temizle
      setSelectedWarehouse(null);
      setWarehouseId(null);
      console.log('ℹ️ Miktar = 0, Depo seçimi temizlendi');
    }
  }, [quantity, warehouses, isEditMode, selectedWarehouse]);

  // NOT: Firebase fallback kaldırıldı - AuthContext'ten companyId alınıyor

  // ✅ branchId parametresi geldiğinde ve branches yüklendiğinde otomatik seç
  useEffect(() => {
    if (open && branchId && branches.length > 0 && !branchesId) {
      const selectedBranchObject = branches.find(branch => branch.id === branchId);
      if (selectedBranchObject) {
        setSelectedBranch(selectedBranchObject);
        setBranchesId(branchId);
        console.log(`Parametre ile gelen şube otomatik seçildi: ${selectedBranchObject.name}`);
      }
    }
    setInitialLoading(false);
  }, [open, branches, branchId, branchesId]);

  // Şube seçim işleyicisi
  const handleBranchChange = (value: LocalBranch | null) => {
    setSelectedBranch(value);
    setBranchesId(value ? value.id : null);
    
    // Şube değiştiğinde seçili kategori, marka ve depoyu sıfırla
    setSelectedCategory(null);
    setCategoryId(null);
    setSelectedBrand(null);
    setBrandId(null);
    setSelectedWarehouse(null);
    setWarehouseId(null);
  };

  // Şube adını bul
  const getBranchName = (branchId: string | null): string => {
    if (!branchId) return 'Tüm Şubeler';
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Bilinmeyen Şube';
  };

  // Kategorileri yükleme (SQL API)
  const loadCategories = async (companyId: string) => {
    try {
      console.log('📦 SQL API\'den kategoriler yükleniyor...');
      const categoriesData = await CategoryService.getAllCategories(companyId);
      
      // SQL'den gelen kategorileri frontend interface'ine dönüştür
      const categoriesList: InventoryCategory[] = categoriesData.map(cat => ({
        id: cat.id,
        name: cat.name,
        parentId: cat.parentId || null,
        level: cat.level,
        createdAt: cat.createdAt,
        branchesId: null // SQL'de branch yok, Firebase'den alınacak
      }));
      
      // Filtreleme: Şube seçiliyse sadece o şubeye ait veya genel kategorileri göster
      let filteredCategories = categoriesList;
      if (branchesId) {
        filteredCategories = categoriesList.filter(category => 
          category.branchesId === branchesId || category.branchesId === null
        );
      }
      
      // Verileri seviyeye göre sıralayalım
      filteredCategories.sort((a, b) => {
        if (a.level !== b.level) {
          return a.level - b.level;
        }
        return a.name.localeCompare(b.name);
      });
      
      setCategories(filteredCategories);
      console.log('✅ SQL API\'den kategoriler yüklendi:', filteredCategories.length);
      
      // Seçili kategori artık filtrelenmiş listede yoksa, seçimi temizle
      if (selectedCategory && !filteredCategories.some(category => category.id === selectedCategory.id)) {
        setSelectedCategory(null);
        setCategoryId(null);
      }
    } catch (error) {
      console.error('❌ SQL API kategori yükleme hatası:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  // Depoları yükleme (SQL API)
  // 🔒 Miktar girildiğinde MERKEZ DEPO otomatik seçilir
  const loadWarehouses = async (companyId: string) => {
    try {
      console.log('📦 SQL API\'den depolar yükleniyor...');
      const [warehousesResult, smartWarehousesResult] = await Promise.allSettled([
        WarehouseService.getAllWarehouses(companyId),
        smartWarehouseService.getAllList()
      ]);
      const warehousesData =
        warehousesResult.status === 'fulfilled' ? warehousesResult.value : [];
      const smartWarehouses =
        smartWarehousesResult.status === 'fulfilled' ? smartWarehousesResult.value : [];
      
      // SQL'den gelen depoları frontend interface'ine dönüştür
      const warehousesList: Warehouse[] = warehousesData.map(wh => ({
        id: wh.id,
        name: wh.name,
        address: wh.address || '',
        description: wh.description || '',
        isDefault: wh.isDefault || false,
        branchesId: null, // SQL'de branch yok
        warehouseType: wh.warehouseType || 'Normal',
      }));

      // Faz 5: Smart depo endpoint'inden gelenleri de birlestir.
      // Bazi ortamlarda inventory warehouse listesi sadece varsayilan depoyu donebildigi icin
      // smart depolari kaybetmemek adina iki kaynagi birlestiriyoruz.
      const existingIds = new Set(warehousesList.map(w => w.id));
      const mergedWarehouses: Warehouse[] = [...warehousesList];
      smartWarehouses.forEach((smartWh) => {
        if (!existingIds.has(smartWh.id)) {
          mergedWarehouses.push({
            id: smartWh.id,
            name: smartWh.warehouseName,
            address: '',
            description: '',
            isDefault: false,
            branchesId: null,
            warehouseType: 'Smart',
          });
          existingIds.add(smartWh.id);
        }
      });

      // Faz 5: Normal + Akıllı depolar birlikte listelensin (varsayılan depo üstte).
      const sortedWarehouses = [...mergedWarehouses].sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return a.name.localeCompare(b.name);
      });

      setWarehouses(sortedWarehouses);
      console.log('✅ Depolar yüklendi:', sortedWarehouses.length);
    } catch (error) {
      console.error('❌ SQL API depo yükleme hatası:', error);
    }
  };

  // Markaları yükleme (SQL API)
  const loadBrands = async (companyId: string) => {
    try {
      console.log('📦 SQL API\'den markalar yükleniyor...');
      const brandsData = await BrandService.getAllBrands(companyId);
      
      // SQL'den gelen markaları frontend interface'ine dönüştür
      const brandsList: Brand[] = brandsData.map(br => ({
        id: br.id,
        name: br.name,
        description: br.description || '',
        website: '', // SQL'de website yok
        branchesId: null // SQL'de branch yok
      }));
      
      // Filtreleme: Şube seçiliyse sadece o şubeye ait veya genel markaları göster
      let filteredBrands = brandsList;
      if (branchesId) {
        filteredBrands = brandsList.filter(brand => 
          brand.branchesId === branchesId || brand.branchesId === null
        );
      }
      
      filteredBrands.sort((a, b) => a.name.localeCompare(b.name));
      
      setBrands(filteredBrands);
      console.log('✅ SQL API\'den markalar yüklendi:', filteredBrands.length);
      
      // Seçili marka artık filtrelenmiş listede yoksa, seçimi temizle
      if (selectedBrand && !filteredBrands.some(brand => brand.id === selectedBrand.id)) {
        setSelectedBrand(null);
        setBrandId(null);
      }
    } catch (error) {
      console.error('❌ SQL API marka yükleme hatası:', error);
    }
  };

  // Para birimlerini yükle (SQL API'den)
  const loadCurrencies = async (companyId: string) => {
    try {
      console.log('🔄 [ADD-ITEM] SQL API\'den para birimleri yükleniyor...');
      
      const response = await accountingApi.get(`/api/currency/settings`);
      
      const currencies: {code: string, active: boolean}[] = [
        { code: 'TRY', active: true }
      ];
      
      const result = response.data;
      
      if (result.success && result.data) {
        Object.entries(result.data).forEach(([code, active]: [string, any]) => {
          if (code !== 'TRY' && active === true) {
            currencies.push({ code, active: true });
          }
        });
      }
      
      console.log('✅ [ADD-ITEM] Aktif para birimleri yüklendi:', currencies.length);
      setAvailableCurrencies(currencies);
      
      // Eğer seçili para birimleri artık mevcut değilse, TRY'ye ayarla
      const purchaseCurrencyExists = currencies.some(c => c.code === purchaseCurrency && c.active);
      const saleCurrencyExists = currencies.some(c => c.code === saleCurrency && c.active);
      
      if (!purchaseCurrencyExists) {
        setPurchaseCurrency('TRY');
      }
      
      if (!saleCurrencyExists) {
        setSaleCurrency('TRY');
      }
      
    } catch (error) {
      console.error('❌ [ADD-ITEM] Para birimi yükleme hatası:', error);
      // Offline fallback: Varsayılan TRY kullan
      setAvailableCurrencies([{ code: 'TRY', active: true }]);
    }
  };

  // ✅ YENİ: Güncelleme fonksiyonu
  const handleUpdate = async () => {
    console.log('🔍 [DEBUG] itemToEdit:', itemToEdit);
    console.log('🔍 [DEBUG] itemToEdit?.id:', itemToEdit?.id);
    
    if (!companyId || !itemToEdit?.id) {
      console.error('❌ [DEBUG] companyId:', companyId, 'itemToEdit?.id:', itemToEdit?.id);
      setError("Ürün güncellenemedi. Lütfen sayfayı yenileyin.");
      return;
    }

    // Validasyon
    if (!itemName.trim() || vatRate === '' || vatStatus === '' ||
        (typeof salePrice === 'string' ? salePrice === '' : salePrice === 0)) {
      setError("Ürün adı, KDV oranı, KDV durumu ve satış fiyatı zorunlu alanlardır.");
      return;
    }

    const unitError = unitsEditorRef.current?.validate() ?? null;
    if (unitError) {
      setError(unitError);
      return;
    }

    setLoading(true);
    
    try {
      // Güncellenecek DTO'yu hazırla
      const updateDto: any = {
        id: itemToEdit.id,
        companyId: companyId,
        name: itemName.trim(),
        barcode: barcode || undefined,
        isDomesticProduct,
        countryOfOrigin: countryOfOrigin || undefined,
        isScale,
        // Tartı işareti kalktıysa kod da düşer; aksi halde ürün tartıda değilken
        // barkodu hâlâ bir koda bağlı kalır
        scaleCode: isScale ? (scaleCode || '') : '',
        shelfLifeDays: (isScale || lotTrackingEnabled) ? (parseInt(shelfLifeDays, 10) || 0) : 0,
        ...(lotTrackingEnabled ? { trackLots, trackExpiry: trackLots && trackExpiry } : {}),
        stockCode: stockCode,
        categoryId: categoryId || undefined,
        brandId: brandId || undefined,
        canBeNegative,
        ...(productionEnabled ? { itemClass, productionMode } : {}),
        isVariantParent,
        parentItemId: null,
        unit,
        
        // Fiyat bilgileri
        purchasePrice: typeof purchasePrice === 'string' ? 
          (purchasePrice === '' ? undefined : Number(purchasePrice)) : 
          (purchasePrice || undefined),
        purchaseCurrency: purchaseCurrency || 'TRY',
        purchaseVendor: purchaseVendor || undefined,
        purchasePrice2: typeof purchasePrice2 === 'string' ? 
          (purchasePrice2 === '' ? undefined : parseFloat(purchasePrice2)) : 
          (purchasePrice2 || undefined),
        purchaseCurrency2: purchaseCurrency2 || 'TRY',
        purchaseVendor2: purchaseVendor2 || undefined,
        purchasePrice3: typeof purchasePrice3 === 'string' ? 
          (purchasePrice3 === '' ? undefined : parseFloat(purchasePrice3)) : 
          (purchasePrice3 || undefined),
        purchaseCurrency3: purchaseCurrency3 || 'TRY',
        purchaseVendor3: purchaseVendor3 || undefined,
        salePrice: typeof salePrice === 'string' ? 
          (salePrice === '' ? 0 : parseFloat(salePrice)) : 
          salePrice,
        saleCurrency: saleCurrency || 'TRY',
        saleCustomer: saleCustomer || undefined,
        salePrice2: typeof salePrice2 === 'string' ? 
          (salePrice2 === '' ? undefined : parseFloat(salePrice2)) : 
          (salePrice2 || undefined),
        saleCurrency2: saleCurrency2 || 'TRY',
        saleCustomer2: saleCustomer2 || undefined,
        salePrice3: typeof salePrice3 === 'string' ? 
          (salePrice3 === '' ? undefined : parseFloat(salePrice3)) : 
          (salePrice3 || undefined),
        saleCurrency3: saleCurrency3 || 'TRY',
        saleCustomer3: saleCustomer3 || undefined,
        listPrice: typeof listPrice === 'string' ? 
          (listPrice === '' ? undefined : parseFloat(listPrice)) : 
          (listPrice || undefined),
        listCurrency: listCurrency || 'TRY',
        
        // Vergi bilgileri
        vatRate: typeof vatRate === 'string' ? 
          (vatRate === '' ? 0 : Number(vatRate)) : 
          vatRate,
        vatStatus: vatStatus || 'excluded',
        sctRate: typeof sctRate === 'string' ? 
          (sctRate === '' ? undefined : Number(sctRate)) : 
          (sctRate || undefined),
        sctStatus: sctStatus || undefined,
        
        // Diğer bilgiler
        description: description || undefined,
        desi: variantDesi === '' ? undefined : Number(variantDesi),
        branchesId: branchesId || undefined,
        departmentNo: departmentNo === '' ? undefined : Number(departmentNo),
        itemType: itemType,
        isActive: true,
        
        // Çoklu resim desteği: ilk görsel kapak, tamamı imageUrls listesi.
        imageUrl: imageUrls[0] || null,
        imageUrls: imageUrls.length > 0 ? imageUrls : null,
      };

      console.log('📝 Ürün güncelleniyor:', updateDto);
      console.log('🏷️ [UPDATE-ITEM] itemType state:', itemType, '| DTO itemType:', updateDto.itemType);
      
      // Update endpoint'i çağır
      const updatedResult = await InventoryService.updateItem(companyId, itemToEdit.id, updateDto);

      // Depo/lokasyon stok kaydini da editte tutarli sekilde guncelle.
      const targetWarehouseId = warehouseId;
      const targetQuantity = quantity === '' ? 0 : Number(quantity);
      const targetLocationId = selectedLocationId || undefined;
      const sourceStocks = Array.isArray(itemToEdit?.warehouseStocks) ? itemToEdit.warehouseStocks : [];
      const sourcePrimaryStock =
        sourceStocks
          .filter((s: any) => Number(s.quantity ?? 0) > 0)
          .sort((a: any, b: any) => Number(b.quantity ?? 0) - Number(a.quantity ?? 0))[0] ||
        sourceStocks[0];
      const sourceWarehouseId = sourcePrimaryStock?.warehouseId || null;
      const sourceLocationId = sourcePrimaryStock?.locationId || undefined;

      if (targetWarehouseId && targetQuantity >= 0) {
        if (isSmartWh && targetLocationId) {
          // Deploy bagimsiz yol:
          // 1) hedef depodaki "genel/legacy" kaydi sifirla
          // 2) secili lokasyona mutlak miktari addStock ile yaz
          await InventoryService.updateWarehouseStockQuantity(
            companyId,
            itemToEdit.id,
            targetWarehouseId,
            0
          );

          if (sourceWarehouseId && sourceWarehouseId !== targetWarehouseId) {
            await InventoryService.updateWarehouseStockQuantity(
              companyId,
              itemToEdit.id,
              sourceWarehouseId,
              0
            );
          }

          if (targetQuantity > 0) {
            await InventoryService.addStockToWarehouse({
              companyId,
              productId: itemToEdit.id,
              warehouseId: targetWarehouseId,
              quantity: targetQuantity,
              locationId: targetLocationId,
            });
          }
        } else {
          // Normal depo veya lokasyonsuz senaryo
          await InventoryService.updateWarehouseStockQuantity(
            companyId,
            itemToEdit.id,
            targetWarehouseId,
            targetQuantity
          );

          if (sourceWarehouseId && sourceWarehouseId !== targetWarehouseId) {
            await InventoryService.updateWarehouseStockQuantity(
              companyId,
              itemToEdit.id,
              sourceWarehouseId,
              0
            );
          }
        }
      }

      // Ek özellikler (toggle kapalıysa mevcut kayıtları temizle)
      const featurePayload: SaveInventoryItemFeatureValueRowDto[] = hasExtraFeatures
        ? itemFeatureValues
            .filter(row => row.featureId)
            .map((row, index) => ({
              featureId: row.featureId,
              value: row.value || null,
              orderIndex: index,
            }))
        : [];

      await ProductFeatureService.saveValuesForItem(itemToEdit.id, featurePayload);

      // Listeyi aninda guncellemek icin parent'a normalize edilmis item gonder.
      // Otorite backend response'udur; isMainItem turevsel alandir.
      if (onItemUpdated) {
        const normalizedUpdatedItem: InventoryItem = {
          ...(itemToEdit as InventoryItem),
          ...updatedResult,
          parentItemId: updatedResult?.parentItemId ?? null,
          isVariantParent: updatedResult?.isVariantParent === true,
          isMainItem: !updatedResult?.parentItemId
        };
        onItemUpdated(normalizedUpdatedItem);
      }

      try {
        const warnings = await unitsEditorRef.current?.save() ?? [];
        if (warnings.length > 0) {
          setUnitNotice({ message: warnings.join(' '), severity: 'info' });
        }
      } catch (unitSaveError: unknown) {
        const message = unitSaveError instanceof Error ? unitSaveError.message : '';
        const text = unitSaveError instanceof InventoryItemUnitError && unitSaveError.code === 'UNIT_IN_USE'
          ? message
          : t('units.saveFailedAfterItem', { ns: 'production', message });
        setError(text);
        setUnitNotice({ message: text, severity: 'error' });
        setLoading(false);
        return;
      }

      console.log('✅ Ürün başarıyla güncellendi!');
      console.log('🏷️ [UPDATE-ITEM] Güncellenen itemType:', updatedResult?.itemType, '| Beklenen:', itemType);
      setLoading(false);
      handleClose();
    } catch (error: any) {
      console.error('❌ Ürün güncellenirken hata:', error);
      const conflict = error?.response?.data?.error;
      if (conflict === 'UNIT_CHANGE_BLOCKED') {
        setError(t('units.baseUnitLocked', { ns: 'production' }));
      } else {
        setError('Ürün güncellenirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
      }
      setLoading(false);
    }
  };

  const handleSave = async (keepOpenForNext: boolean = false) => {
    // ✅ Düzenleme modu kontrolü
    if (isEditMode && itemToEdit) {
      await handleUpdate();
      return;
    }
    
    // Validasyon kontrolü (Yeni ürün için)
    const quantityValue = quantity === '' ? 0 : Number(quantity);
    
    // 🔒 companyId kontrolü - null ise kaydetme
    if (!companyId) {
      console.error('❌ [ADD-ITEM] companyId null, kayıt yapılamıyor!');
      setError("Şirket bilgisi bulunamadı. Lütfen sayfayı yenileyin veya yeniden giriş yapın.");
      return;
    }
    
    // Temel validasyon: Ürün adı, KDV, KDV durumu ve satış fiyatı zorunlu
    if (!itemName.trim() || vatRate === '' || vatStatus === '' ||
        (typeof salePrice === 'string' ? salePrice === '' : salePrice === 0)) {
      setError("Ürün adı, KDV oranı, KDV durumu ve satış fiyatı zorunlu alanlardır.");
      return;
    }
    
    // Eğer miktar > 0 ise depo seçimi zorunlu
    if (quantityValue > 0 && !warehouseId) {
      setError("Miktar girildiğinde depo seçimi zorunludur. Lütfen Merkez Depo'yu seçin.");
      return;
    }

    if (quantityValue > 0 && isSmartWh && !selectedLocationId) {
      setError("Akıllı depoda lokasyon seçimi zorunludur.");
      return;
    }

    // Stok kodu otomatik oluşturma (girilmemişse)
    const inventoryCode = stockCode || `${itemName.substring(0, 2).toUpperCase()}${Math.floor(1000 + Math.random() * 9000)}`;
    
    // Barkod otomatik oluşturma (girilmemişse) — geçerli EAN-13.
    // Etiket basarken gövde 12 haneye kırpılıp kontrol hanesi yeniden
    // hesaplandığı için üretilen değerin baştan geçerli olması şart; aksi
    // halde basılan barkod üründe kayıtlı olandan farklı çıkıyor.
    const generatedBarcode = barcode && barcode.trim() !== ''
      ? barcode
      : generateInternalBarcode();

    // Barkod kontrolü yap (otomatik oluşturulmuş veya girilmiş olsun)
    setSuccessMsg(null);
    setLoading(true);
    try {
      const isDuplicate = await checkDuplicateBarcode(generatedBarcode);
      if (isDuplicate) {
        setLoading(false);
        setIsDuplicateBarcodeModalOpen(true);
        return;
      }
    } catch (error: any) {
      console.error('Barkod kontrolünde hata:', error);
      setError('Barkod kontrolü sırasında bir hata oluştu: ' + error.message);
      setLoading(false);
      return;
    }

    // Varyant özellikleri nesnesini oluştur
    const variantsData: any = {};
    
    if (selectedVariantCategories.includes('physical')) {
      variantsData.physical = {
        color: variantColor || null,
        size: variantSize || null,
        length: variantLength === '' ? null : Number(variantLength),
        width: variantWidth === '' ? null : Number(variantWidth),
        height: variantHeight === '' ? null : Number(variantHeight),
        weight: variantWeight === '' ? null : Number(variantWeight),
        desi: variantDesi === '' ? null : Number(variantDesi),
        thickness: variantThickness === '' ? null : Number(variantThickness)
      };
    }
    
    if (selectedVariantCategories.includes('technical')) {
      variantsData.technical = {
        material: variantMaterial || null,
        grade: variantGrade || null,
        model: variantModel || null,
        series: variantSeries || null,
        power: variantPower === '' ? null : Number(variantPower),
        voltage: variantVoltage === '' ? null : Number(variantVoltage)
      };
    }
    
    if (selectedVariantCategories.includes('design')) {
      variantsData.design = {
        pattern: variantPattern || null,
        style: variantStyle || null,
        finish: variantFinish || null,
        shape: variantShape || null
      };
    }
    
    if (selectedVariantCategories.includes('capacity')) {
      variantsData.capacity = {
        capacity: variantCapacity === '' ? null : Number(variantCapacity),
        diameter: variantDiameter === '' ? null : Number(variantDiameter),
        innerSize: variantInnerSize || null,
        outerSize: variantOuterSize || null
      };
    }
    
    if (selectedVariantCategories.includes('usage')) {
      variantsData.usage = {
        ageGroup: variantAgeGroup || null,
        gender: variantGender || null,
        season: variantSeason || null,
        usage: variantUsage || null
      };
    }
    
    if (selectedVariantCategories.includes('variety')) {
      variantsData.variety = {
        flavor: variantFlavor || null,
        scent: variantScent || null,
        hardness: variantHardness || null,
        transparency: variantTransparency || null
      };
    }
    
    if (selectedVariantCategories.includes('packaging')) {
      variantsData.packaging = {
        packQuantity: variantPackQuantity === '' ? null : Number(variantPackQuantity),
        packType: variantPackType || null,
        packSize: variantPackSize || null
      };
    }
    
    if (selectedVariantCategories.includes('special')) {
      variantsData.special = {
        waterproof: variantWaterproof,
        antibacterial: variantAntibacterial,
        organic: variantOrganic,
        recyclable: variantRecyclable
      };
    }

    // ✅ Depo stok bilgisi (ürün oluşturduktan sonra ayrı eklenecek)
    const warehouseStockQuantity = quantity === '' ? 0 : Number(quantity);
    const selectedWarehouseId = warehouseId; // Null olabilir artık
    
    // SQL API için DTO oluştur
    const newItemDto: CreateInventoryItemDto = {
      companyId: companyId!,
      name: itemName.trim(),
      barcode: generatedBarcode, // ✅ Otomatik oluşturulmuş veya kullanıcı tarafından girilmiş
      isDomesticProduct,
      countryOfOrigin: countryOfOrigin || undefined,
      isScale,
      scaleCode: isScale ? (scaleCode || undefined) : undefined,
      shelfLifeDays: (isScale || lotTrackingEnabled) ? (parseInt(shelfLifeDays, 10) || undefined) : undefined,
      ...(lotTrackingEnabled ? { trackLots, trackExpiry: trackLots && trackExpiry } : {}),
      stockCode: inventoryCode,
      categoryId: categoryId || undefined,
      brandId: brandId || undefined,
      // ❌ KALDIRILDI: warehouseId ve quantity artık CreateWarehouseStockDto ile ayrı gönderilecek
      // warehouseId: warehouseId!,
      // quantity: quantity === '' ? 0 : Number(quantity),
      
      // Stok bilgileri
      canBeNegative,
      ...(productionEnabled ? { itemClass: itemClass || null, productionMode } : {}),
      isVariantParent,
      parentItemId: undefined,
      unit,
      minStockLevel: undefined,
      maxStockLevel: undefined,
      
      // Alış fiyatları
      purchasePrice: typeof purchasePrice === 'string' ? 
        (purchasePrice === '' ? undefined : Number(purchasePrice)) : 
        (purchasePrice || undefined),
      purchaseCurrency: purchaseCurrency || 'TRY',
      purchaseVendor: purchaseVendor || undefined,
      purchasePrice2: typeof purchasePrice2 === 'string' ? 
        (purchasePrice2 === '' ? undefined : Number(purchasePrice2)) : 
        (purchasePrice2 || undefined),
      purchaseCurrency2: purchaseCurrency2 || 'TRY',
      purchaseVendor2: purchaseVendor2 || undefined,
      purchasePrice3: typeof purchasePrice3 === 'string' ? 
        (purchasePrice3 === '' ? undefined : Number(purchasePrice3)) : 
        (purchasePrice3 || undefined),
      purchaseCurrency3: purchaseCurrency3 || 'TRY',
      purchaseVendor3: purchaseVendor3 || undefined,
      
      // Satış fiyatları
      salePrice: typeof salePrice === 'string' ? 
        (salePrice === '' ? 0 : parseFloat(salePrice)) : 
        salePrice,
      saleCurrency: saleCurrency || 'TRY',
      saleCustomer: saleCustomer || undefined,
      salePrice2: typeof salePrice2 === 'string' ? 
        (salePrice2 === '' ? undefined : parseFloat(salePrice2)) : 
        (salePrice2 || undefined),
      saleCurrency2: saleCurrency2 || 'TRY',
      saleCustomer2: saleCustomer2 || undefined,
      salePrice3: typeof salePrice3 === 'string' ? 
        (salePrice3 === '' ? undefined : parseFloat(salePrice3)) : 
        (salePrice3 || undefined),
      saleCurrency3: saleCurrency3 || 'TRY',
      saleCustomer3: saleCustomer3 || undefined,
      listPrice: typeof listPrice === 'string' ? 
        (listPrice === '' ? undefined : parseFloat(listPrice)) : 
        (listPrice || undefined),
      listCurrency: listCurrency || 'TRY',
      
      // Vergi bilgileri
      vatRate: typeof vatRate === 'string' ? 
        (vatRate === '' ? 0 : Number(vatRate)) : 
        vatRate,
      vatStatus: vatStatus || 'excluded',
      sctRate: typeof sctRate === 'string' ? 
        (sctRate === '' ? undefined : Number(sctRate)) : 
        (sctRate || undefined),
      sctStatus: sctStatus || undefined,
      
      // Diğer bilgiler
      description: description || undefined,
      desi: variantDesi === '' ? undefined : Number(variantDesi),
      branchesId: branchesId || undefined, // Firebase'den şube bilgisi
      departmentNo: departmentNo === '' ? undefined : Number(departmentNo),
      itemType: itemType,
      printerGroup: undefined,
      imageUrl: imageUrls[0] || undefined,
      imageUrls: imageUrls.length > 0 ? imageUrls : undefined,
      variants: Object.keys(variantsData).length > 0 ? variantsData : undefined, // ✅ Varyant bilgileri
      isActive: true
    };

    console.log('🚀 SQL API ile ürün kaydediliyor:', newItemDto);
    console.log('🏷️ [ADD-ITEM] itemType state:', itemType, '| DTO itemType:', newItemDto.itemType);
    console.log('📊 Oluşturulan Barkod:', generatedBarcode, '| Stok Kodu:', inventoryCode);
    console.log('📦 Depo Stok Bilgisi:', { warehouseId: selectedWarehouseId, quantity: warehouseStockQuantity });
    
    setLoading(true);
    
    try {
      const createdItem = await InventoryService.createItem(newItemDto);
      console.log('✅ Ürün başarıyla kaydedildi!', createdItem);
      console.log('🏷️ [ADD-ITEM] Kaydedilen itemType:', createdItem.itemType, '| Beklenen:', itemType, '| Eşleşme:', createdItem.itemType === itemType);
      
      // ✅ SADECE EĞER MİKTAR > 0 İSE DEPOYA ÜRÜN EKLE
      if (warehouseStockQuantity > 0 && selectedWarehouseId) {
        try {
          await InventoryService.addStockToWarehouse({
            companyId: companyId!,
            productId: createdItem.id,
            warehouseId: selectedWarehouseId,
            quantity: warehouseStockQuantity,
            locationId: selectedLocationId || undefined,
          });
          console.log('✅ Depoya ürün eklendi:', { 
            productId: createdItem.id, 
            warehouseId: selectedWarehouseId, 
            quantity: warehouseStockQuantity 
          });
        } catch (stockError: any) {
          console.error('❌ Depoya ürün eklenirken hata:', stockError);
          // Stok kaydı oluşturulamazsa ürünü de sil
          await InventoryService.deleteItem(companyId!, createdItem.id);
          throw new Error('Depoya ürün eklenirken hata oluştu: ' + (stockError.response?.data?.message || stockError.message));
        }
      } else {
        console.log('ℹ️ Miktar 0 olduğu için depoya ürün eklenmedi. Ürün deposuz kayıt edildi.');
      }

      const featurePayload: SaveInventoryItemFeatureValueRowDto[] = hasExtraFeatures
        ? itemFeatureValues
            .filter(row => row.featureId)
            .map((row, index) => ({
              featureId: row.featureId,
              value: row.value || null,
              orderIndex: index,
            }))
        : [];

      await ProductFeatureService.saveValuesForItem(createdItem.id, featurePayload);
      
      setLoading(false);
      
      // ✅ YENİ: Ürün başarıyla eklendiyse callback çağır
      if (onItemAdded) {
        onItemAdded(createdItem.id, createdItem.name);
      }
      
      // ✅ "Kaydet ve Yeni Ekle": modalı kapatmadan, ürün adı/barkod/stok kodu
      // dışındaki tüm alanları koruyarak bir sonraki ürün için hazırla.
      if (keepOpenForNext) {
        resetForNextItem();
        setSuccessMsg(`"${createdItem.name}" başarıyla eklendi. Yeni ürün ekleyebilirsiniz.`);
      } else {
        handleClose();
      }
    } catch (error: any) {
      console.error('❌ Ürün kaydedilirken hata:', error);
      setError('Ürün kaydedilirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
      setLoading(false);
    }
  };

  // Barkod çakışma modali kapatma
  const handleCloseDuplicateBarcodeModal = () => {
    setIsDuplicateBarcodeModalOpen(false);
  };

  // Barkod çakışma kontrolü (SQL API)
  const checkDuplicateBarcode = async (barcodeToCheck: string): Promise<boolean> => {
    if (!companyId || !barcodeToCheck.trim()) {
      return false;
    }

    try {
      console.log('🔍 SQL API ile barkod kontrolü yapılıyor:', barcodeToCheck);
      const existingItemData = await InventoryService.checkBarcodeExists(companyId, barcodeToCheck);
      
      if (existingItemData) {
        console.log('❌ Barkod zaten mevcut:', existingItemData);
        // SQL'den gelen veriyi frontend interface'ine dönüştür
        const mappedItem: InventoryItem = {
          id: existingItemData.id,
          name: existingItemData.name,
          stockCode: existingItemData.stockCode,
          barcode: existingItemData.barcode,
          categoryId: existingItemData.categoryId || null,
          brandId: existingItemData.brandId || null,
          // ⚠️ DEPRECATED: quantity ve warehouseId artık kullanılmıyor
          quantity: existingItemData.warehouseStocks?.reduce((sum: number, ws: any) => sum + ws.quantity, 0) || 0,
          warehouseId: existingItemData.warehouseStocks?.[0]?.warehouseId || null,
          unit: existingItemData.unit,
          purchasePrice: existingItemData.purchasePrice || 0,
          purchaseCurrency: existingItemData.purchaseCurrency,
          purchasePrice2: existingItemData.purchasePrice2 || 0,
          purchaseCurrency2: existingItemData.purchaseCurrency2,
          purchasePrice3: existingItemData.purchasePrice3 || 0,
          purchaseCurrency3: existingItemData.purchaseCurrency3,
          purchaseVendor: existingItemData.purchaseVendor || '',
          purchaseVendor2: existingItemData.purchaseVendor2 || '',
          purchaseVendor3: existingItemData.purchaseVendor3 || '',
          salePrice: existingItemData.salePrice,
          saleCurrency: existingItemData.saleCurrency,
          salePrice2: existingItemData.salePrice2 || 0,
          saleCurrency2: existingItemData.saleCurrency2,
          salePrice3: existingItemData.salePrice3 || 0,
          saleCurrency3: existingItemData.saleCurrency3,
          saleCustomer: existingItemData.saleCustomer || '',
          saleCustomer2: existingItemData.saleCustomer2 || '',
          saleCustomer3: existingItemData.saleCustomer3 || '',
          vatRate: existingItemData.vatRate,
          vatStatus: existingItemData.vatStatus,
          sctRate: existingItemData.sctRate,
          sctStatus: existingItemData.sctStatus,
          description: existingItemData.description,
          isActive: existingItemData.isActive,
          createdAt: existingItemData.createdAt,
          branchesId: null,
          parentItemId: null,
          // LEGACY UYUM: parent bagi olmayan kayit main olarak turetilir.
          isMainItem: !existingItemData.parentItemId,
          isVariantParent: existingItemData.isVariantParent ?? false,
          departmentNo: null,
          // ✅ YENİ SİSTEM: WarehouseStocks
          warehouseStocks: existingItemData.warehouseStocks || []
        };
        
        setExistingItem(mappedItem);
        setBarcodeExists(true);
        return true;
      }
      
      console.log('✅ Barkod uygun, kullanılabilir');
      setBarcodeExists(false);
      setExistingItem(null);
      return false;
    } catch (error) {
      console.error('❌ SQL API barkod kontrolü hatası:', error);
      throw error;
    }
  };

  // Dialog kapanma işleyicisi
  const handleClose = () => {
    // Form alanlarını sıfırla
    setSuccessMsg(null);
    setItemName('');
    setBarcode('');
    setStockCode('');
    setIsScale(false);
    setScaleCode('');
    setShelfLifeDays('');
    setTrackLots(false);
    setTrackExpiry(false);
    setCategoryId(null);
    setBrandId(null);
    setQuantity('');
    setCanBeNegative(false);
    setItemClass('');
    setProductionMode('SALE_EXPLODE');
    setIsVariantParent(false);
    setUnit('adet');
    setWarehouseId(null);
    setImageUrls([]);
    setSelectedLocationId(null);
    setLocationOpts([]);
    setPurchasePrice('');
    setPurchaseCurrency('TRY');
    setPurchaseVendor('');
    setPurchasePrice2('');
    setPurchaseCurrency2('TRY');
    setPurchaseVendor2('');
    setPurchasePrice3('');
    setPurchaseCurrency3('TRY');
    setPurchaseVendor3('');
    setSalePrice('');
    setSaleCurrency('TRY');
    setSaleCustomer('');
    setListPrice('');
    setListCurrency('TRY');
    setSalePrice2('');
    setSaleCurrency2('TRY');
    setSaleCustomer2('');
    setSalePrice3('');
    setSaleCurrency3('TRY');
    setSaleCustomer3('');
    setVatRate('');
    setVatStatus('');
    setSctRate('');
    setSctStatus('excluded');
    setDescription('');
    setSelectedCategory(null);
    setSelectedBrand(null);
    setSelectedWarehouse(null);
    setSelectedBranch(null);
    setBranchesId(null);
    setError(null);
    setBarcodeExists(false);
    setExistingItem(null);
    setIsDuplicateBarcodeModalOpen(false);
    setDepartmentNo('');
    setItemType('PRODUCT');
    setHasExtraFeatures(false);
    setItemFeatureValues([]);
    
    // Varyant verilerini sıfırla
    setSelectedVariantCategories([]);
    setVariantColor('');
    setVariantSize('');
    setVariantLength('');
    setVariantWidth('');
    setVariantHeight('');
    setVariantWeight('');
    setVariantDesi('');
    setShowDesiCalculator(false);
    setVariantThickness('');
    setVariantMaterial('');
    setVariantGrade('');
    setVariantModel('');
    setVariantSeries('');
    setVariantPower('');
    setVariantVoltage('');
    setVariantPattern('');
    setVariantStyle('');
    setVariantFinish('');
    setVariantShape('');
    setVariantCapacity('');
    setVariantDiameter('');
    setVariantInnerSize('');
    setVariantOuterSize('');
    setVariantAgeGroup('');
    setVariantGender('');
    setVariantSeason('');
    setVariantUsage('');
    setVariantFlavor('');
    setVariantScent('');
    setVariantHardness('');
    setVariantTransparency('');
    setVariantPackQuantity('');
    setVariantPackType('');
    setVariantPackSize('');
    setVariantWaterproof(null);
    setVariantAntibacterial(null);
    setVariantOrganic(null);
    setVariantRecyclable(null);
    
    // Modalı kapat
    onClose();
  };

  // ✅ "Kaydet ve Yeni Ekle" için yumuşak sıfırlama:
  // Ürünü kaydettikten sonra modalı KAPATMADAN, benzersiz olması gereken
  // alanları (ürün adı, barkod, stok kodu, görsel) temizler; diğer tüm
  // özellikleri (kategori, marka, birim, depo, fiyatlar, KDV/ÖTV, departman,
  // şube, miktar, varyant özellikleri vb.) state'te bırakır ki benzer ürünleri
  // arka arkaya hızlıca eklemek kolay olsun.
  const resetForNextItem = () => {
    setItemName('');
    // Barkod ve stok kodu benzersiz olmalı; boş bırakılırsa bir sonraki
    // kayıtta otomatik üretilir. Korunursa "barkod çakışması" hatası verir.
    setBarcode('');
    setStockCode('');
    // Tartı kodu da benzersiz; bayrak korunuyor çünkü arka arkaya tartılı ürün
    // girmek yaygın, aynı kodu tekrar kaydetmek değil
    setScaleCode('');
    setImageUrls([]);
    // Hata/çakışma durumlarını temizle
    setError(null);
    setBarcodeExists(false);
    setExistingItem(null);
    setIsDuplicateBarcodeModalOpen(false);
  };

  const handleCalculateDesi = () => {
    if (variantLength === '' || variantWidth === '' || variantHeight === '') {
      // Kullanıcıyı genel hata ile boğmak yerine mini hesap alanını aç.
      setShowDesiCalculator(true);
      return;
    }

    // Desi hesaplamasi cm tabanlidir: (Boy x En x Yukseklik) / 3000
    const lengthCm = Number(variantLength);
    const widthCm = Number(variantWidth);
    const heightCm = Number(variantHeight);
    const calculatedDesi = (lengthCm * widthCm * heightCm) / 3000;

    setVariantDesi(Number(calculatedDesi.toFixed(3)));
    setShowDesiCalculator(true);
    setError(null);
  };

  const handleToggleDesiCalculator = () => {
    setShowDesiCalculator(prev => !prev);
  };

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };

  const handleRemoveImageUrl = (url: string) => {
    setImageUrls(prev => prev.filter(x => x !== url));
  };

  const handleAddFeatureRow = () => {
    setItemFeatureValues(prev => ([
      ...prev,
      {
        tempId: `${Date.now()}-${prev.length}`,
        featureId: '',
        value: '',
      },
    ]));
  };

  const handleUpdateFeatureRow = (tempId: string, patch: Partial<ExtraFeatureRow>) => {
    setItemFeatureValues(prev => prev.map(row => (
      row.tempId === tempId ? { ...row, ...patch } : row
    )));
  };

  const handleRemoveFeatureRow = (tempId: string) => {
    setItemFeatureValues(prev => prev.filter(row => row.tempId !== tempId));
  };

  // Kategori seçimi
  const handleCategoryChange = (value: InventoryCategory | null) => {
    setSelectedCategory(value);
    setCategoryId(value ? value.id : null);
  };

  // Lokasyon seçimi (akıllı depo)
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [locationOpts, setLocationOpts] = useState<{ id: string; code: string; name: string }[]>([]);
  const isSmartWh = selectedWarehouse?.warehouseType === 'Smart';
  const locationEmptyForSmart = isSmartWh && locationOpts.length === 0;

  const loadWarehouseLocations = async (warehouseId: string, preserveLocationId?: string | null) => {
    try {
      const r = await accountingApi.get(ACCOUNTING_API.SWL_BY_WAREHOUSE(warehouseId));
      const candidateArrays = [
        r.data?.Data,
        r.data?.data,
        r.data?.Items,
        r.data?.items,
        r.data?.result,
        r.data?.Result,
        r.data?.payload?.data,
        r.data?.payload?.Data,
      ];
      const raw =
        candidateArrays.find((arr) => Array.isArray(arr)) ||
        [];
      const allLocations = (Array.isArray(raw) ? raw : [])
        .map((l: any) => ({
          id: l.id || l.Id,
          code: l.code || l.Code || l.locationCode || l.LocationCode,
          name: l.name || l.Name || l.locationName || l.LocationName,
          type: l.type || l.Type,
          isActive: l.isActive ?? l.IsActive ?? true,
          isPickable: l.isPickable ?? l.IsPickable ?? false,
        }))
        .filter((l) => !!l.id);

      const active = allLocations.filter((l) => l.isActive);
      const pickable = active.filter((l) => l.isPickable);
      const leafTypes = new Set(['bin', 'level', 'rack']);
      const activeLeafLocations = active.filter((l) =>
        leafTypes.has(String(l.type || '').toLowerCase())
      );

      const resolved = pickable.length > 0
        ? pickable
        : (activeLeafLocations.length > 0 ? activeLeafLocations : active);

      const fallbackResolved = resolved.length > 0 ? resolved : allLocations;

      // Çok SKU / çok lokasyon modeli:
      // Akıllı depoda dolu raflar da seçilebilir olmalı.
      // Bu yüzden "sadece boş raf" filtresi kaldırıldı.
      try {
        const stockResp = await InventoryService.getStockByLocation(warehouseId, { page: 1, pageSize: 10000 });
        const stockRows = stockResp?.data || stockResp?.Data || [];

        if (Array.isArray(stockRows)) {
          // Eski davranışta stok satırları dolu raf filtrelemesi için kullanılıyordu.
          // Yeni modelde dolu/boş tüm raflar seçilebilir olduğundan sadece bağlantı
          // kontrolü amacıyla çağrı korunur, içerik filtrede kullanılmaz.
          void stockRows.length;
        }
      } catch {
        // Stok doluluk sorgusu basarisiz olursa, lokasyon listesini oldugu gibi goster.
      }

      const list = fallbackResolved.map(({ id, code, name }) => ({
        id,
        code: code || 'LOC',
        name: name || 'Lokasyon'
      }));

      // Düzenleme modunda mevcut lokasyon filtreye takilsa bile listede gorunsun.
      if (preserveLocationId && !list.some((l) => l.id === preserveLocationId)) {
        const existing = allLocations.find((l) => l.id === preserveLocationId);
        if (existing) {
          list.push({
            id: existing.id,
            code: existing.code || 'LOC',
            name: existing.name || 'Lokasyon'
          });
        }
      }

      setLocationOpts(list);
    } catch {
      setLocationOpts([]);
    }
  };

  // Edit acilisinda da secili smart deponun lokasyonlarini otomatik yukle.
  useEffect(() => {
    if (!open) return;
    if (!selectedWarehouse || selectedWarehouse.warehouseType !== 'Smart') {
      setLocationOpts([]);
      return;
    }

    loadWarehouseLocations(selectedWarehouse.id, selectedLocationId);
  }, [open, selectedWarehouse?.id, selectedWarehouse?.warehouseType]);

  // Depo seçimi
  const handleWarehouseChange = (value: Warehouse | null) => {
    setSelectedWarehouse(value);
    setWarehouseId(value ? value.id : null);
    setSelectedLocationId(null);
    if (!value || value.warehouseType !== 'Smart') {
      setLocationOpts([]);
    }
  };

  // Marka seçimi
  const handleBrandChange = (value: Brand | null) => {
    setSelectedBrand(value);
    setBrandId(value ? value.id : null);
  };

  // Modal açılış-kapanış fonksiyonları
  const handleOpenCategoryModal = () => {
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    // Kategori modalı kapandığında, kategorileri yeniden yükle
    if (companyId) {
      loadCategories(companyId);
    }
  };

  const handleOpenBrandModal = () => {
    setIsBrandModalOpen(true);
  };

  const handleCloseBrandModal = () => {
    setIsBrandModalOpen(false);
    // Marka modalı kapandığında, markaları yeniden yükle
    if (companyId) {
      loadBrands(companyId);
    }
  };

  const handleOpenWarehouseModal = () => {
    setIsWarehouseModalOpen(true);
  };

  const handleCloseWarehouseModal = () => {
    setIsWarehouseModalOpen(false);
    // Depo modalı kapandığında, depoları yeniden yükle
    if (companyId) {
      loadWarehouses(companyId);
    }
  };

  // Kategori tam yolunu göster
  const getCategoryFullPath = (category: InventoryCategory | null): string => {
    if (!category) return '';
    
    const parts: string[] = [category.name];
    let currentParentId = category.parentId;
    
    while (currentParentId) {
      const parentCategory = categories.find(c => c.id === currentParentId);
      if (parentCategory) {
        parts.unshift(parentCategory.name);
        currentParentId = parentCategory.parentId;
      } else {
        break;
      }
    }
    
    return parts.join(' > ');
  };

  // Kategori filtresini geliştirme
  // Kategori tam yolunu almak için bir yardımcı fonksiyon ekleyelim
  const getFullCategoryPath = (categoryId: string): string => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return '';
    
    let path = category.name;
    let currentCat = category;
    
    while (currentCat.parentId) {
      const parentCat = categories.find(cat => cat.id === currentCat.parentId);
      if (!parentCat) break;
      path = `${parentCat.name} > ${path}`;
      currentCat = parentCat;
    }
    
    return path;
  };

  // Filtrelenmiş kategori seçeneklerini hazırlayalım
  const getFilteredCategoryOptions = (): (InventoryCategory & { label: string; isParent: boolean; level: number })[] => {
    return categories.map(category => ({
      ...category,
      name: getFullCategoryPath(category.id),
      label: getFullCategoryPath(category.id),
      level: calculateCategoryLevel(category),
      isParent: categories.some(cat => cat.parentId === category.id)
    }));
  };

  // Kategori seviyesini hesaplamak için
  const calculateCategoryLevel = (category: InventoryCategory): number => {
    let level = 1;
    let currentCat = category;
    
    while (currentCat.parentId) {
      level++;
      const parentCat = categories.find(cat => cat.id === currentCat.parentId);
      if (!parentCat) break;
      currentCat = parentCat;
    }
    
    return level;
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        sx={{ 
          '& .MuiDialog-paper': { 
            borderRadius: 2,
            width: '95vw',
            height: '95vh',
            maxWidth: '1800px',
            margin: '20px'
          } 
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2
          }}
        >
          <Typography variant="h6">{isEditMode ? t('stock.addModal.titleEdit') : t('stock.addModal.titleNew')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={t('stock.addModal.infoBox.tooltip')}>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setShowInfoBox(prev => !prev)}
                sx={{
                  border: '1px solid',
                  borderColor: 'rgba(255,255,255,0.7)',
                  borderRadius: 1,
                  bgcolor: showInfoBox ? 'rgba(255,255,255,0.2)' : 'transparent'
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton edge="end" color="inherit" onClick={handleClose} aria-label={t('stock.common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, height: 'calc(95vh - 120px)', overflow: 'auto' }}>
          {showInfoBox && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('stock.addModal.infoBox.title')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {t('stock.addModal.infoBox.description')}
              </Typography>
              <Typography variant="body2">{t('stock.addModal.infoBox.item1')}</Typography>
              <Typography variant="body2">{t('stock.addModal.infoBox.item2')}</Typography>
              <Typography variant="body2">{t('stock.addModal.infoBox.item3')}</Typography>
            </Alert>
          )}

          {error && error !== "Bu barkod zaten sistemde kayıtlı" && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={1.5}>
              {/* ÜRÜN TİPİ SEÇİCİ */}
              {!isEditMode && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Paper
                      elevation={itemType === 'PRODUCT' ? 4 : 0}
                      onClick={() => setItemType('PRODUCT')}
                      sx={{
                        flex: 1,
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: itemType === 'PRODUCT' ? 'primary.main' : 'divider',
                        bgcolor: itemType === 'PRODUCT' ? 'primary.50' : 'background.paper',
                        borderRadius: 2,
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                      }}
                    >
                      <CategoryIcon sx={{ fontSize: 32, color: itemType === 'PRODUCT' ? 'primary.main' : 'text.secondary', mb: 0.5 }} />
                      <Typography variant="subtitle2" fontWeight="bold" color={itemType === 'PRODUCT' ? 'primary.main' : 'text.primary'}>
                        {t('stock.addModal.itemType.product')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('stock.addModal.itemType.productHint')}
                      </Typography>
                    </Paper>
                    <Paper
                      elevation={itemType === 'SERVICE' ? 4 : 0}
                      onClick={() => setItemType('SERVICE')}
                      sx={{
                        flex: 1,
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: itemType === 'SERVICE' ? 'secondary.main' : 'divider',
                        bgcolor: itemType === 'SERVICE' ? 'secondary.50' : 'background.paper',
                        borderRadius: 2,
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: 'secondary.main', bgcolor: 'action.hover' }
                      }}
                    >
                      <BusinessIcon sx={{ fontSize: 32, color: itemType === 'SERVICE' ? 'secondary.main' : 'text.secondary', mb: 0.5 }} />
                      <Typography variant="subtitle2" fontWeight="bold" color={itemType === 'SERVICE' ? 'secondary.main' : 'text.primary'}>
                        {t('stock.addModal.itemType.service')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('stock.addModal.itemType.serviceHint')}
                      </Typography>
                    </Paper>
                  </Box>
                </Grid>
              )}

              {/* SOL SÜTUN */}
              <Grid item xs={12} lg={6}>
                <Box sx={{ pr: { lg: 2 }, borderRight: { lg: '1px solid', borderColor: 'divider' } }}>
                  {/* Temel Bilgiler */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('stock.addModal.sections.basicInfo')}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isVariantParent}
                          onChange={(e) => setIsVariantParent(e.target.checked)}
                        />
                      }
                      label={t('stock.addModal.fields.variantParent')}
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        label={t('stock.addModal.fields.productName')}
                        value={itemName}
                        onChange={(e) => setItemName(e.target.value)}
                        error={!itemName.trim()}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Barkod"
                        value={barcode}
                        onChange={(e) => {
                          setBarcode(e.target.value);
                          if (barcodeExists) {
                            setBarcodeExists(false);
                            setExistingItem(null);
                            setError(null);
                          }
                        }}
                        error={barcodeExists}
                        helperText={barcodeExists ? "Bu barkod zaten sistemde kayıtlı" : "Boş bırakılırsa otomatik oluşturulur"}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('stock.addModal.fields.stockCode')}
                        value={stockCode}
                        onChange={(e) => setStockCode(e.target.value)}
                        placeholder={t('stock.addModal.fields.stockCodeAuto')}
                        helperText={t('stock.addModal.fields.stockCodeAutoHint')}
                      />
                    </Grid>

                    {/* Menşei ve yerli malı — etikete koşullu basılabilir */}
                    <Grid item xs={12} sm={8}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={COUNTRIES}
                        getOptionLabel={(option) => `${countryCodeToFlag(option.code)}  ${option.name}`}
                        value={COUNTRIES.find((c) => c.code === countryOfOrigin) ?? null}
                        onChange={(_, value) => setCountryOfOrigin(value?.code ?? '')}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        renderInput={(params) => (
                          <TextField {...params} size="small" label="Menşei (Üretim Yeri)" />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isDomesticProduct}
                            onChange={(e) => {
                              setIsDomesticProduct(e.target.checked);
                              // Menşei boşsa Türkiye varsayılır; kullanıcı değiştirebilir.
                              // Dolu bir seçime dokunulmuyor — yerli malı belgesi olan
                              // ürünün menşei başka bir ülke olarak girilmiş olabilir.
                              if (e.target.checked && !countryOfOrigin) {
                                setCountryOfOrigin(TURKEY_CODE);
                              }
                            }}
                          />
                        }
                        label="Yerli Malı"
                        sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                      />
                    </Grid>

                    {/* Tartı — ürün tartıya PLU olarak gönderilir, etiketindeki
                        ağırlık barkodu kasada bu kod üzerinden çözülür */}
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isScale}
                            onChange={(e) => setIsScale(e.target.checked)}
                          />
                        }
                        label="Tartılı Ürün"
                        sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Tartı Kodu"
                        value={scaleCode}
                        disabled={!isScale}
                        onChange={(e) => setScaleCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        placeholder="Örn. 00001"
                        helperText={
                          isScale
                            ? scaleCodeHelperText
                            : 'Ürünü tartılı işaretleyince açılır.'
                        }
                        inputProps={{ inputMode: 'numeric', maxLength: 5 }}
                      />
                    </Grid>

                    {/* Raf ömrü: tartı etikete son tüketim tarihini kendisi
                        hesaplayıp basıyor, tartım günü + (gün - 1) */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Raf Ömrü (gün)"
                        value={shelfLifeDays}
                        disabled={!isScale && !lotTrackingEnabled}
                        onChange={(e) => setShelfLifeDays(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="Örn. 3"
                        helperText={
                          lotTrackingEnabled
                            ? t('shelfHelp', { ns: 'lots' })
                            : isScale
                              ? shelfLifeDaysHelperText
                              : 'Ürünü tartılı işaretleyince açılır.'
                        }
                        inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                      />
                    </Grid>

                    {lotTrackingEnabled && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={<Switch checked={trackLots} onChange={(e) => {
                            setTrackLots(e.target.checked);
                            if (!e.target.checked) setTrackExpiry(false);
                          }} />}
                          label={t('trackLots', { ns: 'lots' })}
                        />
                        <FormControlLabel
                          control={<Switch checked={trackExpiry} disabled={!trackLots} onChange={(e) => setTrackExpiry(e.target.checked)} />}
                          label={t('trackExpiry', { ns: 'lots' })}
                        />
                        {trackLots && !(itemToEdit as any)?.trackLots && !(itemToEdit as any)?.TrackLots && (
                          <FormHelperText>{t('openingNote', { ns: 'lots' })}</FormHelperText>
                        )}
                      </Grid>
                    )}

                    {/* Kategori, Depo, Marka - Tek Satırda */}
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={getFilteredCategoryOptions()}
                          getOptionLabel={(option) => option.name}
                          value={selectedCategory}
                          onChange={(_, value) => handleCategoryChange(value)}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('stock.addModal.fields.category')}
                              size="small"
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ 
                                pl: (option.level - 1) * 2, 
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                fontWeight: option.isParent ? 'bold' : 'normal',
                                color: option.isParent ? 'text.primary' : 'text.secondary'
                              }}>
                                <CategoryIcon 
                                  fontSize="small" 
                                  sx={{ 
                                    mr: 1, 
                                    color: option.isParent ? 'primary.main' : 'action.active',
                                    opacity: option.isParent ? 1 : 0.7
                                  }} 
                                />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: option.isParent ? 'bold' : 'normal'
                                  }}
                                >
                                  {option.label}
                                </Typography>
                              </Box>
                            </li>
                          )}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleOpenCategoryModal}
                          sx={{ 
                            minWidth: '36px', 
                            height: '40px',
                            padding: 0,
                            borderRadius: '4px'
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={warehouses}
                          getOptionLabel={(option) => option.name}
                          value={selectedWarehouse}
                          onChange={(_, value) => handleWarehouseChange(value)}
                          disabled={(quantity === '' || Number(quantity) === 0)} // 🔒 Miktar = 0 ise depo seçimi devre dışı
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={
                                (quantity === '' || Number(quantity) === 0) 
                                  ? t('stock.addModal.fields.warehouseNoQuantity')
                                  : t('stock.addModal.fields.warehouse')
                              }
                              error={Number(quantity) > 0 && !warehouseId}
                              size="small"
                              helperText={
                                (quantity === '' || Number(quantity) === 0)
                                  ? t('stock.addModal.fields.warehouseNoQuantityHint')
                                  : t('stock.addModal.fields.warehouseHint')
                              }
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                fontWeight: option.isDefault ? 'bold' : 'normal'
                              }}>
                                <WarehouseIcon 
                                  fontSize="small" 
                                  sx={{ 
                                    mr: 1, 
                                    color: option.isDefault ? 'primary.main' : 'action.active'
                                  }} 
                                />
                                <Typography variant="body2">
                                  {option.name}
                                  {option.isDefault && ` (${t('stock.quickAdd.default')})`}
                                </Typography>
                                <Chip
                                  label={option.warehouseType === 'Smart' ? t('stock.addModal.fields.smartWarehouse') : t('stock.addModal.fields.normalWarehouse')}
                                  size="small"
                                  variant="outlined"
                                  color={option.warehouseType === 'Smart' ? 'info' : 'default'}
                                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            </li>
                          )}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleOpenWarehouseModal}
                          sx={{ 
                            minWidth: '36px', 
                            height: '40px',
                            padding: 0,
                            borderRadius: '4px'
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={locationOpts}
                        disabled={!isSmartWh}
                        getOptionLabel={(option) => option.code || option.name || ''}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2">{option.code}</Typography>
                              {option.name && option.name !== option.code && (
                                <Typography variant="caption" color="text.secondary">{option.name}</Typography>
                              )}
                            </Box>
                          </li>
                        )}
                        value={locationOpts.find(l => l.id === selectedLocationId) || null}
                        onChange={(_, value) => setSelectedLocationId(value ? value.id : null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={isSmartWh ? t('stock.addModal.fields.locationRequired') : t('stock.addModal.fields.location')}
                            size="small"
                            error={isSmartWh && !selectedLocationId && Number(quantity) > 0}
                            helperText={
                              !isSmartWh
                                ? t('stock.addModal.fields.locationOptionalHint')
                                : (locationEmptyForSmart
                                  ? t('stock.addModal.fields.locationNotFoundHint')
                                  : (!selectedLocationId && Number(quantity) > 0
                                  ? t('stock.addModal.fields.locationRequiredHint')
                                  : ''))
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={brands}
                          getOptionLabel={(option) => option.name}
                          value={selectedBrand}
                          onChange={(_, value) => handleBrandChange(value)}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('stock.addModal.fields.brand')}
                              size="small"
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%'
                              }}>
                                <BrandingWatermarkIcon 
                                  fontSize="small" 
                                  sx={{ 
                                    mr: 1, 
                                    color: 'action.active'
                                  }} 
                                />
                                <Typography variant="body2">
                                  {option.name}
                                </Typography>
                              </Box>
                            </li>
                          )}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleOpenBrandModal}
                          sx={{ 
                            minWidth: '36px', 
                            height: '40px',
                            padding: 0,
                            borderRadius: '4px'
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <Tooltip 
                        title={isEditMode ? t('stock.addModal.fields.quantityEditHint') : ""}
                        arrow
                        placement="top"
                      >
                        <TextField
                          fullWidth
                          label={t('stock.addModal.fields.quantity')}
                          variant="outlined"
                          size="small"
                          value={quantity}
                          onChange={(e) => {
                            const newValue = e.target.value === '' ? '' : Number(e.target.value);
                            setQuantity(newValue);
                          }}
                          type="number"
                          disabled={isEditMode} // ✅ Düzenleme modunda pasif
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <FormControl variant="standard" sx={{ minWidth: 70 }}>
                                  <Select
                                    value={unit}
                                    onChange={(e) => setUnit(e.target.value)}
                                    sx={{ ml: 1 }}
                                  >
                                    {unitOptions.map(option => (
                                      <MenuItem key={option} value={option}>
                                        {getUnitLabel(option)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </InputAdornment>
                            ),
                            inputProps: { min: 0 }
                          }}
                          helperText={[
                            isEditMode ? t('stock.addModal.fields.quantityEditableFromTable') : '',
                            baseUnitLocked ? t('units.baseUnitLocked', { ns: 'production' }) : '',
                          ].filter(Boolean).join(' ') || undefined}
                          FormHelperTextProps={baseUnitLocked ? { sx: { color: 'warning.main' } } : undefined}
                        />
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <TextField
                          fullWidth
                          label="Desi"
                          variant="outlined"
                          size="small"
                          type="number"
                          value={variantDesi}
                          onChange={(e) => setVariantDesi(e.target.value === '' ? '' : Number(e.target.value))}
                          inputProps={{ min: 0, step: '0.001' }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">desi</InputAdornment>
                          }}
                        />
                        <Button
                          size="small"
                          variant="text"
                          onClick={handleToggleDesiCalculator}
                          endIcon={showDesiCalculator ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          sx={{ minWidth: 80, px: 0.75 }}
                        >
                          Hesapla
                        </Button>
                      </Box>
                    </Grid>

                    {showDesiCalculator && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.25,
                            mt: 0.25,
                            backgroundColor: 'background.paper'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Desi hesaplama: (Boy/Uzunluk[cm] x Genişlik[cm] x Yükseklik[cm]) / 3000
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Boy/Uzunluk"
                                value={variantLength}
                                onChange={(e) => setVariantLength(e.target.value === '' ? '' : Number(e.target.value))}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">cm</InputAdornment>
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Genişlik"
                                value={variantWidth}
                                onChange={(e) => setVariantWidth(e.target.value === '' ? '' : Number(e.target.value))}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">cm</InputAdornment>
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Yükseklik"
                                value={variantHeight}
                                onChange={(e) => setVariantHeight(e.target.value === '' ? '' : Number(e.target.value))}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">cm</InputAdornment>
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button fullWidth size="small" variant="contained" onClick={handleCalculateDesi}>
                                  Hesapla
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={canBeNegative}
                            onChange={(e) => setCanBeNegative(e.target.checked)}
                          />
                        }
                        label={t('stock.quickAdd.fields.canBeNegative')}
                        sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                      />
                    </Grid>

                    {productionEnabled && itemType === 'PRODUCT' && (
                      <>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{t('stockCard.itemClass', { ns: 'production' })}</InputLabel>
                            <Select
                              label={t('stockCard.itemClass', { ns: 'production' })}
                              value={itemClass}
                              onChange={(e) => setItemClass(e.target.value)}
                            >
                              <MenuItem value="">{t('stockCard.unspecified', { ns: 'production' })}</MenuItem>
                              <MenuItem value="RAW">{t('stockCard.classes.RAW', { ns: 'production' })}</MenuItem>
                              <MenuItem value="SEMI">{t('stockCard.classes.SEMI', { ns: 'production' })}</MenuItem>
                              <MenuItem value="FINISHED">{t('stockCard.classes.FINISHED', { ns: 'production' })}</MenuItem>
                              <MenuItem value="PACKAGING">{t('stockCard.classes.PACKAGING', { ns: 'production' })}</MenuItem>
                              <MenuItem value="BYPRODUCT">{t('stockCard.classes.BYPRODUCT', { ns: 'production' })}</MenuItem>
                              <MenuItem value="TRADE">{t('stockCard.classes.TRADE', { ns: 'production' })}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{t('stockCard.productionMode', { ns: 'production' })}</InputLabel>
                            <Select
                              label={t('stockCard.productionMode', { ns: 'production' })}
                              value={productionMode}
                              onChange={(e) => setProductionMode(e.target.value as 'SALE_EXPLODE' | 'MAKE_TO_STOCK')}
                            >
                              <MenuItem value="SALE_EXPLODE">{t('stockCard.modes.SALE_EXPLODE', { ns: 'production' })}</MenuItem>
                              <MenuItem value="MAKE_TO_STOCK">{t('stockCard.modes.MAKE_TO_STOCK', { ns: 'production' })}</MenuItem>
                            </Select>
                            <FormHelperText>
                              {t(`stockCard.help.${productionMode}`, { ns: 'production' })}
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <ItemUnitsEditor
                            ref={unitsEditorRef}
                            itemId={isEditMode ? itemToEdit?.id : null}
                            baseUnit={unit}
                            onBaseUnitLocked={setBaseUnitLocked}
                          />
                        </Grid>
                      </>
                    )}

                  </Grid>

                  {/* Fiyat Bilgileri */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 1.5 }}>
                    {t('stock.addModal.sections.priceInfo')}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
              
                  <Grid container spacing={1.5}>
                    {/* Alış Fiyatları 1-2-3 */}
                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('stock.addModal.fields.purchasePrice1')}
                            value={purchasePrice}
                            onChange={(e) => setPurchasePrice(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FormControl variant="standard" sx={{ minWidth: 60 }}>
                                    <Select
                                      value={purchaseCurrency}
                                      onChange={(e) => setPurchaseCurrency(e.target.value)}
                                    >
                                      {availableCurrencies
                                        .filter(currency => currency.active)
                                        .map(currency => (
                                          <MenuItem key={currency.code} value={currency.code}>
                                            {getCurrencySymbol(currency.code)}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('stock.addModal.fields.supplier1')}
                            value={purchaseVendor}
                            onChange={(e) => setPurchaseVendor(e.target.value)}
                            placeholder={t('stock.addModal.fields.supplierNamePlaceholder')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('stock.addModal.fields.purchasePrice2')}
                            value={purchasePrice2}
                            onChange={(e) => setPurchasePrice2(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FormControl variant="standard" sx={{ minWidth: 60 }}>
                                    <Select
                                      value={purchaseCurrency2}
                                      onChange={(e) => setPurchaseCurrency2(e.target.value)}
                                    >
                                      {availableCurrencies
                                        .filter(currency => currency.active)
                                        .map(currency => (
                                          <MenuItem key={currency.code} value={currency.code}>
                                            {getCurrencySymbol(currency.code)}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('stock.addModal.fields.supplier2')}
                            value={purchaseVendor2}
                            onChange={(e) => setPurchaseVendor2(e.target.value)}
                            placeholder={t('stock.addModal.fields.supplierNamePlaceholder')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('stock.addModal.fields.purchasePrice3')}
                            value={purchasePrice3}
                            onChange={(e) => setPurchasePrice3(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FormControl variant="standard" sx={{ minWidth: 60 }}>
                                    <Select
                                      value={purchaseCurrency3}
                                      onChange={(e) => setPurchaseCurrency3(e.target.value)}
                                    >
                                      {availableCurrencies
                                        .filter(currency => currency.active)
                                        .map(currency => (
                                          <MenuItem key={currency.code} value={currency.code}>
                                            {getCurrencySymbol(currency.code)}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('stock.addModal.fields.supplier3')}
                            value={purchaseVendor3}
                            onChange={(e) => setPurchaseVendor3(e.target.value)}
                            placeholder={t('stock.addModal.fields.supplierNamePlaceholder')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Satış Fiyatları 1-2-3 */}
                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required
                            size="small"
                            type="number"
                            label={t('stock.addModal.fields.salePrice1')}
                            value={salePrice}
                            onChange={(e) => setSalePrice(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                            error={salePrice === ''}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FormControl variant="standard" sx={{ minWidth: 60 }}>
                                    <Select
                                      value={saleCurrency}
                                      onChange={(e) => setSaleCurrency(e.target.value)}
                                    >
                                      {availableCurrencies
                                        .filter(currency => currency.active)
                                        .map(currency => (
                                          <MenuItem key={currency.code} value={currency.code}>
                                            {getCurrencySymbol(currency.code)}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('stock.addModal.fields.customerGroup1')}
                            value={saleCustomer}
                            onChange={(e) => setSaleCustomer(e.target.value)}
                            placeholder={t('stock.addModal.fields.customerNamePlaceholder')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('stock.addModal.fields.salePrice2')}
                            value={salePrice2}
                            onChange={(e) => setSalePrice2(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FormControl variant="standard" sx={{ minWidth: 60 }}>
                                    <Select
                                      value={saleCurrency2}
                                      onChange={(e) => setSaleCurrency2(e.target.value)}
                                    >
                                      {availableCurrencies
                                        .filter(currency => currency.active)
                                        .map(currency => (
                                          <MenuItem key={currency.code} value={currency.code}>
                                            {getCurrencySymbol(currency.code)}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('stock.addModal.fields.customerGroup2')}
                            value={saleCustomer2}
                            onChange={(e) => setSaleCustomer2(e.target.value)}
                            placeholder={t('stock.addModal.fields.customerNamePlaceholder')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('stock.addModal.fields.salePrice3')}
                            value={salePrice3}
                            onChange={(e) => setSalePrice3(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FormControl variant="standard" sx={{ minWidth: 60 }}>
                                    <Select
                                      value={saleCurrency3}
                                      onChange={(e) => setSaleCurrency3(e.target.value)}
                                    >
                                      {availableCurrencies
                                        .filter(currency => currency.active)
                                        .map(currency => (
                                          <MenuItem key={currency.code} value={currency.code}>
                                            {getCurrencySymbol(currency.code)}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  </FormControl>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('stock.addModal.fields.customerGroup3')}
                            value={saleCustomer3}
                            onChange={(e) => setSaleCustomer3(e.target.value)}
                            placeholder={t('stock.addModal.fields.customerNamePlaceholder')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

              {/* Liste Fiyatı - En Alta Taşındı */}
                    <Grid item xs={12}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('stock.addModal.fields.listPrice')}
                            value={listPrice}
                            onChange={(e) => setListPrice(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FormControl variant="standard" sx={{ minWidth: 60 }}>
                                    <Select
                                      value={listCurrency}
                                      onChange={(e) => setListCurrency(e.target.value)}
                                    >
                                      {availableCurrencies
                                        .filter(currency => currency.active)
                                        .map(currency => (
                                          <MenuItem key={currency.code} value={currency.code}>
                                            {getCurrencySymbol(currency.code)}
                                          </MenuItem>
                                        ))
                                      }
                                    </Select>
                                  </FormControl>
                                </InputAdornment>
                              )
                            }}
                            helperText={t('stock.addModal.fields.listPriceHint')}
                          />
                        </Grid>
                </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* SAĞ SÜTUN */}
              <Grid item xs={12} lg={6}>
                <Box sx={{ pl: { lg: 2 } }}>
                  {/* Şube Seçimi */}
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    {t('stock.addModal.sections.branchSelection')}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={branches}
                        getOptionLabel={(option) => option.name}
                        value={selectedBranch}
                        onChange={(_, newValue) => handleBranchChange(newValue)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('stock.addModal.fields.selectBranchOptional')}
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                                  {params.InputProps.startAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              fontWeight: option.isHeadquarter ? 'bold' : 'normal'
                            }}>
                              <BusinessIcon 
                                fontSize="small" 
                                sx={{ 
                                  mr: 1, 
                                  color: option.isHeadquarter ? 'primary.main' : 'action.active'
                                }} 
                              />
                              <Typography variant="body2">
                                {option.name}
                                {option.isHeadquarter && ' (Merkez)'}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 1.5 }}>
                    {t('stock.addModal.sections.imageUpload')}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsImageLibraryModalOpen(true)}
                      >
                        {t('stock.addModal.actions.addImage')}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          minHeight: 120,
                          bgcolor: 'background.default'
                        }}
                      >
                        {imageUrls.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('stock.addModal.image.noImageSelected')}
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                            {imageUrls.map((url, index) => (
                              <Box
                                key={`${url}-${index}`}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.6
                                }}
                              >
                                <Typography variant="body2" sx={{ pr: 1 }}>
                                  {index + 1}. {index === 0 ? t('stock.addModal.image.coverImage') : t('stock.addModal.image.image')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button size="small" component="a" href={url} target="_blank">
                                    {t('stock.addModal.actions.show')}
                                  </Button>
                                  <Button size="small" color="error" onClick={() => handleRemoveImageUrl(url)}>
                                    {t('stock.addModal.actions.remove')}
                                  </Button>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                  
                  {showLegacyVariantFields && (
                  <Grid container spacing={1.5}>
                    {/* Varyant Kategori Seçimi */}
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        size="small"
                        options={variantCategoryOptions}
                        getOptionLabel={(option) => option.label}
                        value={variantCategoryOptions.filter(option => selectedVariantCategories.includes(option.value))}
                        onChange={(_, value) => setSelectedVariantCategories(value.map(v => v.value))}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Varyant Kategorileri Seçin"
                            placeholder="Özellik kategorilerini seçin..."
                            size="small"
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option.label}
                              {...getTagProps({ index })}
                              size="small"
                            />
                          ))
                        }
                      />
                    </Grid>

              {/* Fiziksel Özellikler */}
              {selectedVariantCategories.includes('physical') && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 1, mb: 1 }}>
                      Fiziksel Özellikler
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Renk</InputLabel>
                      <Select
                        value={variantColor}
                        onChange={(e) => setVariantColor(e.target.value)}
                        label="Renk"
                      >
                        {colorOptions.map(color => (
                          <MenuItem key={color} value={color}>{color}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Beden</InputLabel>
                      <Select
                        value={variantSize}
                        onChange={(e) => setVariantSize(e.target.value)}
                        label="Beden"
                      >
                        {sizeOptions.map(size => (
                          <MenuItem key={size} value={size}>{size}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Boy/Uzunluk"
                      value={variantLength}
                      onChange={(e) => setVariantLength(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">cm</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Genişlik"
                      value={variantWidth}
                      onChange={(e) => setVariantWidth(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">cm</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Yükseklik"
                      value={variantHeight}
                      onChange={(e) => setVariantHeight(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">cm</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Ağırlık"
                      value={variantWeight}
                      onChange={(e) => setVariantWeight(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">kg</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Kalınlık"
                      value={variantThickness}
                      onChange={(e) => setVariantThickness(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">mm</InputAdornment>
                      }}
                    />
                  </Grid>
                </>
              )}

              {/* Teknik Özellikler */}
              {selectedVariantCategories.includes('technical') && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 1, mb: 1 }}>
                      Teknik Özellikler
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Malzeme"
                      value={variantMaterial}
                      onChange={(e) => setVariantMaterial(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Kalite/Sınıf</InputLabel>
                      <Select
                        value={variantGrade}
                        onChange={(e) => setVariantGrade(e.target.value)}
                        label="Kalite/Sınıf"
                      >
                        {gradeOptions.map(grade => (
                          <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Model"
                      value={variantModel}
                      onChange={(e) => setVariantModel(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Seri"
                      value={variantSeries}
                      onChange={(e) => setVariantSeries(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Güç"
                      value={variantPower}
                      onChange={(e) => setVariantPower(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">W</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Voltaj"
                      value={variantVoltage}
                      onChange={(e) => setVariantVoltage(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">V</InputAdornment>
                      }}
                    />
                  </Grid>
                </>
              )}

              {/* Tasarım Özellikleri */}
              {selectedVariantCategories.includes('design') && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 1, mb: 1 }}>
                      Tasarım Özellikleri
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Desen</InputLabel>
                      <Select
                        value={variantPattern}
                        onChange={(e) => setVariantPattern(e.target.value)}
                        label="Desen"
                      >
                        {patternOptions.map(pattern => (
                          <MenuItem key={pattern} value={pattern}>{pattern}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Stil</InputLabel>
                      <Select
                        value={variantStyle}
                        onChange={(e) => setVariantStyle(e.target.value)}
                        label="Stil"
                      >
                        {styleOptions.map(style => (
                          <MenuItem key={style} value={style}>{style}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Finish/Kaplama</InputLabel>
                      <Select
                        value={variantFinish}
                        onChange={(e) => setVariantFinish(e.target.value)}
                        label="Finish/Kaplama"
                      >
                        {finishOptions.map(finish => (
                          <MenuItem key={finish} value={finish}>{finish}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Şekil</InputLabel>
                      <Select
                        value={variantShape}
                        onChange={(e) => setVariantShape(e.target.value)}
                        label="Şekil"
                      >
                        {shapeOptions.map(shape => (
                          <MenuItem key={shape} value={shape}>{shape}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Kapasite ve Boyut */}
              {selectedVariantCategories.includes('capacity') && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 1, mb: 1 }}>
                      Kapasite ve Boyut
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Kapasite"
                      value={variantCapacity}
                      onChange={(e) => setVariantCapacity(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">L</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Çap"
                      value={variantDiameter}
                      onChange={(e) => setVariantDiameter(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">cm</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="İç Boyut"
                      value={variantInnerSize}
                      onChange={(e) => setVariantInnerSize(e.target.value)}
                      placeholder="ör: 20x30x40 cm"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Dış Boyut"
                      value={variantOuterSize}
                      onChange={(e) => setVariantOuterSize(e.target.value)}
                      placeholder="ör: 25x35x45 cm"
                    />
                  </Grid>
                </>
              )}

              {/* Kullanım Özellikleri */}
              {selectedVariantCategories.includes('usage') && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 1, mb: 1 }}>
                      Kullanım Özellikleri
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Yaş Grubu</InputLabel>
                      <Select
                        value={variantAgeGroup}
                        onChange={(e) => setVariantAgeGroup(e.target.value)}
                        label="Yaş Grubu"
                      >
                        {ageGroupOptions.map(age => (
                          <MenuItem key={age} value={age}>{age}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Cinsiyet</InputLabel>
                      <Select
                        value={variantGender}
                        onChange={(e) => setVariantGender(e.target.value)}
                        label="Cinsiyet"
                      >
                        {genderOptions.map(gender => (
                          <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Mevsim</InputLabel>
                      <Select
                        value={variantSeason}
                        onChange={(e) => setVariantSeason(e.target.value)}
                        label="Mevsim"
                      >
                        {seasonOptions.map(season => (
                          <MenuItem key={season} value={season}>{season}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Kullanım Amacı</InputLabel>
                      <Select
                        value={variantUsage}
                        onChange={(e) => setVariantUsage(e.target.value)}
                        label="Kullanım Amacı"
                      >
                        {usageOptions.map(usage => (
                          <MenuItem key={usage} value={usage}>{usage}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Ürün Çeşitleri */}
              {selectedVariantCategories.includes('variety') && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 1, mb: 1 }}>
                      Ürün Çeşitleri
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Aroma/Tat"
                      value={variantFlavor}
                      onChange={(e) => setVariantFlavor(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Parfüm/Koku"
                      value={variantScent}
                      onChange={(e) => setVariantScent(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sertlik</InputLabel>
                      <Select
                        value={variantHardness}
                        onChange={(e) => setVariantHardness(e.target.value)}
                        label="Sertlik"
                      >
                        {hardnessOptions.map(hardness => (
                          <MenuItem key={hardness} value={hardness}>{hardness}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Şeffaflık</InputLabel>
                      <Select
                        value={variantTransparency}
                        onChange={(e) => setVariantTransparency(e.target.value)}
                        label="Şeffaflık"
                      >
                        {transparencyOptions.map(transparency => (
                          <MenuItem key={transparency} value={transparency}>{transparency}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Paketleme Özellikleri */}
              {selectedVariantCategories.includes('packaging') && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 1, mb: 1 }}>
                      Paketleme Özellikleri
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Paket Adedi"
                      value={variantPackQuantity}
                      onChange={(e) => setVariantPackQuantity(e.target.value === '' ? '' : Number(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">adet</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Paket Tipi</InputLabel>
                      <Select
                        value={variantPackType}
                        onChange={(e) => setVariantPackType(e.target.value)}
                        label="Paket Tipi"
                      >
                        {packTypeOptions.map(type => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Paket Boyutu</InputLabel>
                      <Select
                        value={variantPackSize}
                        onChange={(e) => setVariantPackSize(e.target.value)}
                        label="Paket Boyutu"
                      >
                        {packSizeOptions.map(size => (
                          <MenuItem key={size} value={size}>{size}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Özel Özellikler */}
              {selectedVariantCategories.includes('special') && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 1, mb: 1 }}>
                      Özel Özellikler
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={variantWaterproof === true}
                          onChange={(e) => setVariantWaterproof(e.target.checked ? true : null)}
                        />
                      }
                      label="Su Geçirmez"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={variantAntibacterial === true}
                          onChange={(e) => setVariantAntibacterial(e.target.checked ? true : null)}
                        />
                      }
                      label="Antibakteriyel"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={variantOrganic === true}
                          onChange={(e) => setVariantOrganic(e.target.checked ? true : null)}
                        />
                      }
                      label="Organik"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={variantRecyclable === true}
                          onChange={(e) => setVariantRecyclable(e.target.checked ? true : null)}
                        />
                      }
                      label="Geri Dönüştürülebilir"
                    />
                  </Grid>
                </>
              )}
                  </Grid>
                  )}

                  <Grid container spacing={1.5}>
                    {/* Vergi Bilgileri */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 1.5 }}>
                        {t('stock.addModal.sections.taxInfo')}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        type="number"
                        label={t('stock.addModal.fields.vatRate')}
                        value={vatRate}
                        onChange={(e) => setVatRate(e.target.value === '' ? '' : Number(e.target.value))}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        error={vatRate === ''}
                      />
                      <FormControl 
                        fullWidth 
                        size="small" 
                        sx={{ mt: 1 }}
                        required
                        error={vatStatus === ''} // ✅ YENİ: Hata gösterimi
                      >
                        <InputLabel>{t('stock.addModal.fields.vatStatusRequired')}</InputLabel>
                        <Select
                          value={vatStatus}
                          onChange={(e) => setVatStatus(e.target.value)}
                          label={t('stock.addModal.fields.vatStatusRequired')}
                        >
                          <MenuItem value="included">{t('stock.quickAdd.vatIncluded')}</MenuItem>
                          <MenuItem value="excluded">{t('stock.quickAdd.vatExcluded')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t('stock.addModal.fields.sctRate')}
                        value={sctRate}
                        onChange={(e) => setSctRate(e.target.value === '' ? '' : Number(e.target.value))}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                      />
                      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <InputLabel>{t('stock.addModal.fields.sctStatus')}</InputLabel>
                        <Select
                          value={sctStatus}
                          onChange={(e) => setSctStatus(e.target.value)}
                          label={t('stock.addModal.fields.sctStatus')}
                        >
                          <MenuItem value="included">{t('stock.addModal.fields.sctIncluded')}</MenuItem>
                          <MenuItem value="excluded">{t('stock.addModal.fields.sctExcluded')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Ek Özellikler */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 1.5 }}>
                        {t('stock.addModal.sections.extraFeatures')}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={hasExtraFeatures}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setHasExtraFeatures(checked);
                              if (!checked) {
                                setItemFeatureValues([]);
                              } else if (checked && itemFeatureValues.length === 0) {
                                handleAddFeatureRow();
                              }
                            }}
                          />
                        }
                        label={t('stock.addModal.fields.configureExtraFeatures')}
                      />
                    </Grid>

                    {hasExtraFeatures && (
                      <Grid item xs={12}>
                        {availableFeatures.length === 0 ? (
                          <Alert severity="info">
                            {t('stock.addModal.fields.noExtraFeatureDefinition')}
                          </Alert>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {itemFeatureValues.map((row) => {
                              const selectedFeature = availableFeatures.find(f => f.id === row.featureId) || null;
                              const selectedIds = itemFeatureValues.map(v => v.featureId).filter(Boolean);
                              const options = availableFeatures.filter(
                                f => !selectedIds.includes(f.id) || f.id === row.featureId
                              );

                              return (
                                <Grid container spacing={1.5} key={row.tempId}>
                                  <Grid item xs={12} sm={5}>
                                    <Autocomplete
                                      fullWidth
                                      size="small"
                                      options={options}
                                      value={selectedFeature}
                                      getOptionLabel={(option) => option.name}
                                      onChange={(_, value) =>
                                        handleUpdateFeatureRow(row.tempId, { featureId: value?.id || '' })
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label={t('stock.addModal.fields.extraFeature')}
                                        />
                                      )}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label={t('stock.addModal.fields.extraFeatureValue')}
                                      value={row.value}
                                      onChange={(e) =>
                                        handleUpdateFeatureRow(row.tempId, { value: e.target.value })
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={1}>
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => handleRemoveFeatureRow(row.tempId)}
                                      sx={{ mt: { xs: 0, sm: 0.5 } }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              );
                            })}

                            <Box>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddFeatureRow}
                              >
                                {t('stock.addModal.fields.addExtraFeatureRow')}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Grid>
                    )}

                    {/* Ek Bilgiler */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 1.5 }}>
                        {t('stock.addModal.sections.extraInfo')}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        label={t('stock.addModal.fields.description')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t('stock.addModal.fields.departmentNo')}
                        value={departmentNo}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || (Number(value) >= 1 && Number(value) <= 10)) {
                            setDepartmentNo(value === '' ? '' : Number(value));
                          }
                        }}
                        InputProps={{
                          inputProps: { 
                            min: 1, 
                            max: 10,
                            step: 1
                          }
                        }}
                        helperText={t('stock.addModal.fields.departmentNoHint')}
                      />
                      <Typography variant="caption" display="block" color="textSecondary" sx={{mt: 0.5}}>
                        {t('stock.addModal.fields.departmentNoInfo')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined" size="small">{t('stock.common.cancel')}</Button>
          {/* ✅ "Kaydet ve Yeni Ekle": sadece yeni ürün modunda göster. Kaydeder,
              modalı kapatmaz; ürün adı/barkod/stok kodu hariç alanları korur. */}
          {!isEditMode && (
            <Button
              onClick={() => handleSave(true)}
              variant="outlined"
              size="small"
              disabled={
                loading ||
                !itemName.trim() ||
                vatRate === '' ||
                vatStatus === '' ||
                (typeof salePrice === 'string' ? salePrice === '' : salePrice === 0) ||
                barcodeExists ||
                (Number(quantity) > 0 && !warehouseId)
              }
            >
              {loading ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  {t('stock.common.saving')}
                </>
              ) : (
                t('stock.common.saveAndNew', 'Kaydet ve Yeni Ekle')
              )}
            </Button>
          )}
          <Button 
            onClick={() => handleSave(false)} 
            variant="contained"
            size="small"
            disabled={
              loading || 
              !itemName.trim() || 
              vatRate === '' || 
              vatStatus === '' || // ✅ YENİ: KDV Durumu zorunlu
              (typeof salePrice === 'string' ? salePrice === '' : salePrice === 0) || 
              barcodeExists ||
              // Miktar > 0 ise depo seçimi zorunlu
              (Number(quantity) > 0 && !warehouseId)
            }
          >
            {loading ? (
              <>
                <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                {isEditMode ? t('stock.common.updating') : t('stock.common.saving')}
              </>
            ) : (
              isEditMode ? t('stock.common.update') : t('stock.common.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ "Kaydet ve Yeni Ekle" başarı bildirimi */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      >
        <Alert
          onClose={() => setSuccessMsg(null)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!unitNotice}
        autoHideDuration={6000}
        onClose={() => setUnitNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      >
        <Alert
          onClose={() => setUnitNotice(null)}
          severity={unitNotice?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {unitNotice?.message}
        </Alert>
      </Snackbar>

      {/* Barkod Çakışması Modal */}
      <Dialog 
        open={isDuplicateBarcodeModalOpen} 
        onClose={handleCloseDuplicateBarcodeModal}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'error.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ErrorOutlineIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Barkod Çakışması</Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={handleCloseDuplicateBarcodeModal} aria-label="kapat">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 1 }}>
          {existingItem && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>"{barcode}"</strong> barkodlu ürün zaten sistemde kayıtlı. Aynı barkoda sahip farklı bir ürün ekleyemezsiniz.
              </Typography>
              
              <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Mevcut Ürün Bilgileri:
                </Typography>
                <Divider sx={{ mb: 1 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Ürün Adı:</Typography>
                    <Typography variant="body1" fontWeight="medium">{existingItem.name}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Stok Kodu:</Typography>
                    <Typography variant="body1" fontWeight="medium">{existingItem.stockCode}</Typography>
                  </Grid>
                  
                  {existingItem.categoryId && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Kategori:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {categories.find(c => c.id === existingItem.categoryId)?.name || 'Bilinmiyor'}
                      </Typography>
                    </Grid>
                  )}
                  
                  {existingItem.warehouseId && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Depo:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {warehouses.find(w => w.id === existingItem.warehouseId)?.name || 'Bilinmiyor'}
                      </Typography>
                    </Grid>
                  )}
                  
                  {existingItem.branchesId && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Şube:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {branches.find(b => b.id === existingItem.branchesId)?.name || 'Bilinmiyor'}
                      </Typography>
                    </Grid>
                  )}
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Miktar:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {existingItem.quantity} {existingItem.unit}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
              
              <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Lütfen farklı bir barkod girin veya başka bir ürün seçin.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDuplicateBarcodeModal} 
            variant="contained" 
            color="primary" 
            size="medium"
            fullWidth
          >
            Tamam
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal bileşenleri */}
      <InventoryCategoryModal
        open={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        branchId={branchesId ? branchesId : undefined}
      />
      
      <InventoryBrandModal
        open={isBrandModalOpen}
        onClose={handleCloseBrandModal}
        branchId={branchesId ? branchesId : undefined}
      />
      
      <InventoryWarehouseModal
        open={isWarehouseModalOpen}
        onClose={handleCloseWarehouseModal}
        branchId={branchesId ? branchesId : undefined}
      />

      <ImageLibrarySelectionModal
        open={isImageLibraryModalOpen}
        onClose={() => setIsImageLibraryModalOpen(false)}
        onSave={(selectedUrls) => {
          setImageUrls(selectedUrls);
          setIsImageLibraryModalOpen(false);
        }}
        productId={itemToEdit?.id || null}
        productName={itemName || ''}
        currentImageUrls={imageUrls}
        companyId={companyId}
      />
    </>
  );
};

export default AddInventoryItem; 